import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stage, Layer, Rect, Line, Text, Group, Transformer, Circle } from 'react-konva';
import { API_BASE, apiFetch, getStoredUser, showAppAlert, showAppConfirm } from './api';
import { theme } from './theme';

const DEFAULT_STAGE = { width: 1400, height: 800 };
const DEFAULT_LAYOUT = { version: 2, stage: DEFAULT_STAGE, elements: [] };
const MIN_VIEW_SCALE = 0.25;
const MAX_VIEW_SCALE = 4;
const ZOOM_STEP = 1.08;
const LAYOUT_EXPORT_FORMAT = 'dispoboard-warehouse-layout';
const AUTOSAVE_DELAY_MS = 3000;
const DUPLICATE_OFFSET = 24;
const HISTORY_LIMIT = 80;
const SNAP_SCREEN_THRESHOLD = 8;
const MIN_SNAP_THRESHOLD = 3;
const MAX_SNAP_THRESHOLD = 18;
const SNAP_LINE_PADDING = 80;
const EMPTY_TEXT_HANDLE_SIZE = 18;
const EDITOR_ACTIVE_WINDOW_MS = 15000;
const EDIT_SESSION_HEARTBEAT_MS = 10000;
const ROTATION_SNAP_ANGLES = [0, 45, 90, 135, 180, 225, 270, 315];
const ROTATION_SNAP_TOLERANCE = 4;
const LOCAL_DRAFT_STORAGE_PREFIX = 'dispoboard_warehouse_layout_draft_v1';
const SERVICE_RETRY_MS = 60000;
const SAVE_UNAVAILABLE_MESSAGE = 'Aktuell kann nicht gespeichert werden. Der Service wird vermutlich gerade aktualisiert oder neu gestartet. Bitte in ca. 1 Minute erneut versuchen.';
const FLOORS = [
  { id: 0, label: '0 Basis' },
  { id: 1, label: '1 Empore' },
  { id: 2, label: '2 Dachgeschoss' },
];

const RECT_PATTERN_OPTIONS = [
  { value: 'none', label: 'Kein Muster' },
  { value: 'diagonal', label: 'Gestreift diagonal' },
  { value: 'vertical', label: 'Gestreift vertikal' },
  { value: 'horizontal', label: 'Gestreift horizontal' },
  { value: 'grid', label: 'Kariert' },
  { value: 'crosshatch', label: 'Kreuzschraffur' },
  { value: 'dots', label: 'Gepunktet' },
];
const RECT_PATTERN_VALUES = new Set(RECT_PATTERN_OPTIONS.map(option => option.value));

const ELEMENT_LABELS = {
  gate: 'Gate',
  loadingBay: 'Ladezone',
  rect: 'Rechteck',
  line: 'Linie',
  text: 'Text',
};

function createElementId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `layout-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
}

function createElement(type, gates, loadingBays, floor = 0, position = {}) {
  const id = createElementId();
  const base = {
    id,
    type,
    x: Number.isFinite(position.x) ? position.x : 120,
    y: Number.isFinite(position.y) ? position.y : 120,
    rotation: 0,
    floor,
    label: ELEMENT_LABELS[type],
  };

  if (type === 'gate') {
    const gate = gates[0];
    return {
      ...base,
      width: 120,
      height: 56,
      color: '#039eff',
      gateId: gate?.id || '',
      label: gate?.name || 'Tor',
    };
  }

  if (type === 'loadingBay') {
    const loadingBay = loadingBays[0];
    return {
      ...base,
      width: 120,
      height: 56,
      color: '#f97316',
      loadingBayId: loadingBay?.id || '',
      label: loadingBay?.name || 'Ladezone',
    };
  }

  if (type === 'rect') {
    return { ...base, width: 180, height: 90, color: '#334155', text: '', fontSize: 18, textColor: '#fff', pattern: 'none', patternColor: '#94a3b8', patternSpacing: 18, label: 'Rechteck' };
  }

  if (type === 'line') {
    return { ...base, points: [0, 0, 180, 0], stroke: '#f8fafc', label: 'Linie' };
  }

  return { ...base, width: 220, height: 60, text: 'Text', color: '#f8fafc', fontSize: 28, label: 'Text' };
}

function normalizeFloor(value) {
  const floor = Number(value);
  return [0, 1, 2].includes(floor) ? floor : 0;
}

function isElementVisibleOnFloor(element, floor) {
  return element?.allFloors === true || normalizeFloor(element?.floor) === normalizeFloor(floor);
}

function normalizeElement(element) {
  if (!element || typeof element !== 'object' || !element.id || !element.type) return null;
  return {
    ...element,
    floor: normalizeFloor(element.floor),
    allFloors: element.allFloors === true,
    rotation: normalizeRotation(element.rotation),
  };
}

function normalizeLayout(value) {
  const source = value && typeof value === 'object' ? value : DEFAULT_LAYOUT;
  const stage = source.stage && typeof source.stage === 'object' ? source.stage : DEFAULT_STAGE;
  return {
    version: 2,
    stage: {
      width: Number(stage.width) || DEFAULT_STAGE.width,
      height: Number(stage.height) || DEFAULT_STAGE.height,
    },
    elements: Array.isArray(source.elements) ? source.elements.map(normalizeElement).filter(Boolean) : [],
  };
}

function getLayoutFromImport(value) {
  const candidate = value?.layout && typeof value.layout === 'object' ? value.layout : value;
  if (!candidate || typeof candidate !== 'object' || !candidate.stage || !Array.isArray(candidate.elements)) {
    throw new Error('Invalid layout file');
  }

  return normalizeLayout(candidate);
}

function createLayoutExport(layout) {
  return {
    format: LAYOUT_EXPORT_FORMAT,
    exportedAt: new Date().toISOString(),
    layout: normalizeLayout(layout),
  };
}

function getExportFileName() {
  const date = new Date().toISOString().slice(0, 10);
  return `warehouse-layout-${date}.json`;
}

function getLocalDraftKey() {
  const user = getStoredUser();
  const owner = user?.id || user?.username || 'anonymous';
  return `${LOCAL_DRAFT_STORAGE_PREFIX}:${owner}`;
}

function readLocalDraft() {
  try {
    const raw = window.localStorage.getItem(getLocalDraftKey());
    if (!raw) return null;
    const draft = JSON.parse(raw);
    if (!draft?.layout) return null;
    return {
      ...draft,
      layout: normalizeLayout(draft.layout),
    };
  } catch {
    return null;
  }
}

function writeLocalDraft(draft) {
  try {
    window.localStorage.setItem(getLocalDraftKey(), JSON.stringify({
      ...draft,
      layout: normalizeLayout(draft.layout),
      savedAt: new Date().toISOString(),
    }));
  } catch {
    // Local draft persistence is best-effort; the in-memory editor state still remains active.
  }
}

function clearLocalDraft() {
  try {
    window.localStorage.removeItem(getLocalDraftKey());
  } catch {
    // Ignore storage cleanup errors.
  }
}

function isServiceUnavailableError(error) {
  if (error?.code === 'SERVICE_UNAVAILABLE') return true;
  if (error?.name === 'TypeError') return true;
  return /failed to fetch|network|load failed|fetch/i.test(String(error?.message || ''));
}

function createServiceUnavailableError() {
  const error = new Error(SAVE_UNAVAILABLE_MESSAGE);
  error.code = 'SERVICE_UNAVAILABLE';
  return error;
}

function toNumber(value, fallback = 0) {
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
}

function normalizeSelectionBox(start, end) {
  return {
    x: Math.min(start.x, end.x),
    y: Math.min(start.y, end.y),
    width: Math.abs(end.x - start.x),
    height: Math.abs(end.y - start.y),
  };
}

function boxesIntersect(a, b) {
  return (
    a.x <= b.x + b.width
    && a.x + a.width >= b.x
    && a.y <= b.y + b.height
    && a.y + a.height >= b.y
  );
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function getCircularAngleDistance(a, b) {
  const diff = Math.abs(a - b) % 360;
  return Math.min(diff, 360 - diff);
}

function normalizeRotation(value, options = {}) {
  const snap = options.snap !== false;
  let angle = Number(value);
  if (!Number.isFinite(angle)) angle = 0;
  angle = ((angle % 360) + 360) % 360;

  if (snap) {
    const snapAngle = ROTATION_SNAP_ANGLES.find(candidate => (
      getCircularAngleDistance(angle, candidate) <= ROTATION_SNAP_TOLERANCE
    ));
    if (snapAngle != null) return snapAngle;
  }

  const rounded = Math.round(angle * 1000) / 1000;
  return Object.is(rounded, -0) ? 0 : rounded;
}

function getRotationDelta(start, current) {
  let delta = normalizeRotation(current, { snap: false }) - normalizeRotation(start, { snap: false });
  if (delta > 180) delta -= 360;
  if (delta < -180) delta += 360;
  return delta;
}

function hasSelectionModifier(event) {
  return Boolean(event.shiftKey || event.ctrlKey || event.metaKey);
}

function isEditableTarget(target) {
  const tagName = target?.tagName;
  return tagName === 'INPUT' || tagName === 'SELECT' || tagName === 'TEXTAREA' || target?.isContentEditable;
}

function getRectText(element) {
  return String(element.text || '');
}

function normalizeRectPattern(value) {
  return RECT_PATTERN_VALUES.has(value) ? value : 'none';
}

function getClippedDiagonalPatternPoints(width, height, constant, direction = 1) {
  const candidates = direction === 1
    ? [
        { x: 0, y: constant },
        { x: width, y: constant - width },
        { x: constant, y: 0 },
        { x: constant - height, y: height },
      ]
    : [
        { x: 0, y: constant },
        { x: width, y: width + constant },
        { x: -constant, y: 0 },
        { x: height - constant, y: height },
      ];

  const points = [];
  candidates.forEach(point => {
    if (point.x < -0.001 || point.x > width + 0.001 || point.y < -0.001 || point.y > height + 0.001) return;
    const next = {
      x: clamp(point.x, 0, width),
      y: clamp(point.y, 0, height),
    };
    if (!points.some(existing => Math.abs(existing.x - next.x) < 0.001 && Math.abs(existing.y - next.y) < 0.001)) {
      points.push(next);
    }
  });

  if (points.length < 2) return null;
  return [points[0].x, points[0].y, points[1].x, points[1].y];
}

function getRectPatternElement(element, width, height) {
  const pattern = normalizeRectPattern(element.pattern);
  if (pattern === 'none') return null;

  const color = element.patternColor || '#94a3b8';
  const spacing = clamp(Number(element.patternSpacing) || 18, 6, 80);
  const strokeWidth = clamp(Number(element.patternStrokeWidth) || 1, 1, 8);
  const patternNodes = [];

  const addVerticalLines = () => {
    for (let x = spacing; x < width; x += spacing) {
      patternNodes.push(<Line key={`v-${x}`} points={[x, 0, x, height]} stroke={color} strokeWidth={strokeWidth} listening={false} />);
    }
  };

  const addHorizontalLines = () => {
    for (let y = spacing; y < height; y += spacing) {
      patternNodes.push(<Line key={`h-${y}`} points={[0, y, width, y]} stroke={color} strokeWidth={strokeWidth} listening={false} />);
    }
  };

  const addDiagonalLines = (direction = 1) => {
    const start = direction === 1 ? 0 : -width;
    const end = direction === 1 ? width + height : height;
    for (let constant = start; constant <= end; constant += spacing) {
      const points = getClippedDiagonalPatternPoints(width, height, constant, direction);
      if (points) {
        patternNodes.push(<Line key={`d-${direction}-${constant}`} points={points} stroke={color} strokeWidth={strokeWidth} listening={false} />);
      }
    }
  };

  if (pattern === 'vertical') addVerticalLines();
  if (pattern === 'horizontal') addHorizontalLines();
  if (pattern === 'grid') {
    addVerticalLines();
    addHorizontalLines();
  }
  if (pattern === 'diagonal') addDiagonalLines(1);
  if (pattern === 'crosshatch') {
    addDiagonalLines(1);
    addDiagonalLines(-1);
  }
  if (pattern === 'dots') {
    const radius = clamp(spacing * 0.14, 1.5, 4);
    for (let y = spacing / 2; y < height; y += spacing) {
      for (let x = spacing / 2; x < width; x += spacing) {
        patternNodes.push(<Circle key={`dot-${x}-${y}`} x={x} y={y} radius={radius} fill={color} listening={false} />);
      }
    }
  }

  return (
    <Group clipX={0} clipY={0} clipWidth={width} clipHeight={height} listening={false}>
      {patternNodes}
    </Group>
  );
}

function getTextElementText(element) {
  if (Object.prototype.hasOwnProperty.call(element, 'text')) return String(element.text || '');
  return String(element.label || 'Text');
}

function getTextMetrics(element) {
  const text = element.type === 'rect' ? getRectText(element) : getTextElementText(element);
  const fontSize = Math.max(8, Number(element.fontSize) || 28);
  const lines = String(text || '').split('\n');
  const longestLineWidth = Math.max(0, ...lines.map(line => line.length * fontSize * 0.72 + fontSize * 0.35));
  const width = Math.max(
    text ? 8 : EMPTY_TEXT_HANDLE_SIZE,
    longestLineWidth
  );
  const height = text ? Math.max(fontSize * 1.35, lines.length * fontSize * 1.35) : EMPTY_TEXT_HANDLE_SIZE;
  return { text, width, height, fontSize };
}

function shouldKeepTextUpright(element, text) {
  const width = Number(element.width) || 0;
  const height = Number(element.height) || 0;
  const fontSize = Math.max(12, Number(element.fontSize) || Math.min(20, height * 0.34));
  const estimatedWidth = String(text || '').length * fontSize * 0.58;
  return width >= 70 && height >= fontSize * 1.7 && estimatedWidth <= Math.max(40, width - 12);
}

function autosaveLabelFor(date) {
  if (!date) return 'Noch nicht automatisch gespeichert';
  const diffMs = Math.max(0, Date.now() - date.getTime());
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'Zuletzt automatisch gespeichert gerade eben';
  if (minutes === 1) return 'Zuletzt automatisch gespeichert vor 1 Minute';
  return `Zuletzt automatisch gespeichert vor ${minutes} Minuten`;
}

function rotatePoint(point, degrees = 0) {
  if (!degrees) return point;
  const radians = degrees * Math.PI / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return {
    x: point.x * cos - point.y * sin,
    y: point.x * sin + point.y * cos,
  };
}

function unrotatePoint(point, degrees = 0) {
  return rotatePoint(point, -degrees);
}

function localLinePointToAbsolute(element, point) {
  const rotated = rotatePoint(point, element.rotation || 0);
  return {
    x: (element.x || 0) + rotated.x,
    y: (element.y || 0) + rotated.y,
  };
}

function absoluteLinePointToLocal(element, point) {
  return unrotatePoint({
    x: point.x - (element.x || 0),
    y: point.y - (element.y || 0),
  }, element.rotation || 0);
}

function getLinePoints(element) {
  const points = Array.isArray(element.points) ? element.points : [0, 0, 180, 0];
  const start = rotatePoint({ x: Number(points[0]) || 0, y: Number(points[1]) || 0 }, element.rotation || 0);
  const end = rotatePoint({ x: Number(points[2]) || 0, y: Number(points[3]) || 0 }, element.rotation || 0);
  return [
    { x: (element.x || 0) + start.x, y: (element.y || 0) + start.y },
    { x: (element.x || 0) + end.x, y: (element.y || 0) + end.y },
  ];
}

function getLineLocalPoints(element) {
  const points = Array.isArray(element.points) ? element.points : [0, 0, 180, 0];
  return [
    { x: Number(points[0]) || 0, y: Number(points[1]) || 0 },
    { x: Number(points[2]) || 0, y: Number(points[3]) || 0 },
  ];
}

function getLineLength(element) {
  const [start, end] = getLineLocalPoints(element);
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  return Math.sqrt(dx * dx + dy * dy);
}

function setLineLength(element, length) {
  const [start, end] = getLineLocalPoints(element);
  const currentLength = getLineLength(element) || 1;
  const nextLength = Math.max(1, Number(length) || 1);
  const dx = (end.x - start.x) / currentLength;
  const dy = (end.y - start.y) / currentLength;
  return [start.x, start.y, start.x + dx * nextLength, start.y + dy * nextLength];
}

function projectPointToLineAxis(element, endpointIndex, localPoint) {
  const localPoints = getLineLocalPoints(element);
  const fixedPoint = localPoints[endpointIndex === 0 ? 1 : 0];
  const originalMovingPoint = localPoints[endpointIndex];
  const axis = {
    x: originalMovingPoint.x - fixedPoint.x,
    y: originalMovingPoint.y - fixedPoint.y,
  };
  const axisLength = Math.sqrt(axis.x * axis.x + axis.y * axis.y) || 1;
  const unit = {
    x: axis.x / axisLength,
    y: axis.y / axisLength,
  };
  const pointerVector = {
    x: localPoint.x - fixedPoint.x,
    y: localPoint.y - fixedPoint.y,
  };
  const projectedLength = Math.max(1, pointerVector.x * unit.x + pointerVector.y * unit.y);
  return {
    point: {
      x: fixedPoint.x + unit.x * projectedLength,
      y: fixedPoint.y + unit.y * projectedLength,
    },
    fixedPoint,
    unit,
    length: projectedLength,
  };
}

function getElementBox(element) {
  if (element.type === 'line') {
    const points = getLinePoints(element);
    const xs = points.map(point => point.x);
    const ys = points.map(point => point.y);
    const x = Math.min(...xs);
    const y = Math.min(...ys);
    const width = Math.max(1, Math.max(...xs) - x);
    const height = Math.max(1, Math.max(...ys) - y);
    return { x, y, width, height };
  }

  if (element.type === 'text') {
    const metrics = getTextMetrics(element);
    return {
      x: element.x || 0,
      y: element.y || 0,
      width: metrics.width,
      height: metrics.height,
    };
  }

  return {
    x: element.x || 0,
    y: element.y || 0,
    width: Math.max(1, Number(element.width) || 1),
    height: Math.max(1, Number(element.height) || 1),
  };
}

function getSnapAnchors(element) {
  const box = getElementBox(element);
  const anchors = {
    x: [
      { kind: 'left', value: box.x },
      { kind: 'centerX', value: box.x + box.width / 2 },
      { kind: 'right', value: box.x + box.width },
    ],
    y: [
      { kind: 'top', value: box.y },
      { kind: 'centerY', value: box.y + box.height / 2 },
      { kind: 'bottom', value: box.y + box.height },
    ],
    box,
  };

  if (element.type === 'text') {
    const metrics = getTextMetrics(element);
    anchors.y.push({ kind: 'baseline', value: box.y + metrics.fontSize * 0.86 });
  }

  if (element.type === 'line') {
    const [start, end] = getLinePoints(element);
    anchors.linePoints = [start, end];
    anchors.x.push({ kind: 'lineStartX', value: start.x }, { kind: 'lineEndX', value: end.x });
    anchors.y.push({ kind: 'lineStartY', value: start.y }, { kind: 'lineEndY', value: end.y });
  }

  return anchors;
}

function getDraggedElementSnapshot(element, position) {
  return { ...element, x: position.x, y: position.y };
}

function getBoxesBounds(boxes) {
  if (!boxes.length) return null;
  const left = Math.min(...boxes.map(box => box.x));
  const top = Math.min(...boxes.map(box => box.y));
  const right = Math.max(...boxes.map(box => box.x + box.width));
  const bottom = Math.max(...boxes.map(box => box.y + box.height));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

function getElementSpawnFrame(element) {
  if (element.type === 'line') {
    const points = element.points || [0, 0, 180, 0];
    const xs = [Number(points[0]) || 0, Number(points[2]) || 0];
    const ys = [Number(points[1]) || 0, Number(points[3]) || 0];
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    return {
      offsetX: minX,
      offsetY: minY,
      width: Math.max(1, Math.max(...xs) - minX),
      height: Math.max(1, Math.max(...ys) - minY),
    };
  }

  if (element.type === 'text') {
    const metrics = getTextMetrics(element);
    return { offsetX: 0, offsetY: 0, width: metrics.width, height: metrics.height };
  }

  return {
    offsetX: 0,
    offsetY: 0,
    width: Math.max(1, Number(element.width) || 1),
    height: Math.max(1, Number(element.height) || 1),
  };
}

function getSnapThreshold(scale) {
  return clamp(SNAP_SCREEN_THRESHOLD / Math.max(0.1, scale), MIN_SNAP_THRESHOLD, MAX_SNAP_THRESHOLD);
}

function getLineEndpointSnap(element, endpointIndex, localPoint, otherElements, scale, event) {
  const projected = projectPointToLineAxis(element, endpointIndex, localPoint);
  if (event?.ctrlKey || event?.metaKey) {
    return { point: projected.point, lines: [] };
  }

  const threshold = getSnapThreshold(scale);
  const movingAbs = localLinePointToAbsolute(element, projected.point);
  let bestLength = null;

  otherElements
    .filter(other => other.type === 'line' && other.id !== element.id)
    .forEach(other => {
      getLinePoints(other).forEach(point => {
        const localTarget = absoluteLinePointToLocal(element, point);
        const targetVector = {
          x: localTarget.x - projected.fixedPoint.x,
          y: localTarget.y - projected.fixedPoint.y,
        };
        const targetLength = targetVector.x * projected.unit.x + targetVector.y * projected.unit.y;
        if (targetLength > 0) {
          const nearestPointOnAxis = {
            x: projected.fixedPoint.x + projected.unit.x * targetLength,
            y: projected.fixedPoint.y + projected.unit.y * targetLength,
          };
          const nearestAbs = localLinePointToAbsolute(element, nearestPointOnAxis);
          const distance = Math.sqrt((nearestAbs.x - point.x) ** 2 + (nearestAbs.y - point.y) ** 2);
          const lengthDiff = targetLength - projected.length;
          if (distance <= threshold && Math.abs(lengthDiff) <= threshold && (!bestLength || Math.abs(lengthDiff) < bestLength.distance)) {
            bestLength = { distance: Math.abs(lengthDiff), value: targetLength };
          }
        }
      });

      const targetLength = getLineLength(other);
      const diffLength = targetLength - projected.length;
      if (Math.abs(diffLength) <= threshold && (!bestLength || Math.abs(diffLength) < bestLength.distance)) {
        bestLength = { distance: Math.abs(diffLength), value: targetLength };
      }
    });

  let snappedLocal = projected.point;
  if (bestLength) {
    snappedLocal = {
      x: projected.fixedPoint.x + projected.unit.x * bestLength.value,
      y: projected.fixedPoint.y + projected.unit.y * bestLength.value,
    };
  }

  const snappedAbs = localLinePointToAbsolute(element, snappedLocal);
  const fixedAbs = localLinePointToAbsolute(element, projected.fixedPoint);
  return {
    point: snappedLocal,
    lines: [
      ...(bestLength ? [{ axis: 'length', value: fixedAbs.y, x1: fixedAbs.x, x2: snappedAbs.x }] : []),
    ],
  };
}

function overlapsCrossAxis(a, b, axis) {
  if (axis === 'x') {
    return Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y) > 0;
  }
  return Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x) > 0;
}

function getSpacingGuide(axis, from, to, movingBox) {
  if (axis === 'x') {
    const y = clamp(
      (Math.max(from.y, movingBox.y) + Math.min(from.y + from.height, movingBox.y + movingBox.height)) / 2,
      Math.min(from.y, movingBox.y),
      Math.max(from.y + from.height, movingBox.y + movingBox.height)
    );
    return { axis: 'spacingX', y, x1: from.x + from.width, x2: to };
  }

  const x = clamp(
    (Math.max(from.x, movingBox.x) + Math.min(from.x + from.width, movingBox.x + movingBox.width)) / 2,
    Math.min(from.x, movingBox.x),
    Math.max(from.x + from.width, movingBox.x + movingBox.width)
  );
  return { axis: 'spacingY', x, y1: from.y + from.height, y2: to };
}

function addEqualSpacingCandidates(axis, draggedElements, otherElements, threshold, candidates) {
  const movingBoxes = draggedElements.map(getElementBox);
  const movingGroupBox = getBoxesBounds(movingBoxes);
  const boxesToSnap = movingGroupBox && movingBoxes.length > 1 ? [...movingBoxes, movingGroupBox] : movingBoxes;
  const otherBoxes = otherElements.map(getElementBox);

  for (let firstIndex = 0; firstIndex < otherBoxes.length; firstIndex += 1) {
    for (let secondIndex = firstIndex + 1; secondIndex < otherBoxes.length; secondIndex += 1) {
      const rawA = otherBoxes[firstIndex];
      const rawB = otherBoxes[secondIndex];
      const a = axis === 'x'
        ? (rawA.x <= rawB.x ? rawA : rawB)
        : (rawA.y <= rawB.y ? rawA : rawB);
      const b = a === rawA ? rawB : rawA;
      if (!overlapsCrossAxis(a, b, axis)) continue;

      const gap = axis === 'x'
        ? b.x - (a.x + a.width)
        : b.y - (a.y + a.height);
      if (gap < 0) continue;

      boxesToSnap.forEach(movingBox => {
        if (!overlapsCrossAxis(movingBox, a, axis) && !overlapsCrossAxis(movingBox, b, axis)) return;

        if (axis === 'x') {
          const rightTargetLeft = b.x + b.width + gap;
          const rightDiff = rightTargetLeft - movingBox.x;
          if (Math.abs(rightDiff) <= threshold) {
            candidates.x.push({
              diff: rightDiff,
              distance: Math.abs(rightDiff) + 0.2,
              lines: [getSpacingGuide('x', b, rightTargetLeft, { ...movingBox, x: rightTargetLeft })],
            });
          }

          const leftTargetLeft = a.x - gap - movingBox.width;
          const leftDiff = leftTargetLeft - movingBox.x;
          if (Math.abs(leftDiff) <= threshold) {
            candidates.x.push({
              diff: leftDiff,
              distance: Math.abs(leftDiff) + 0.2,
              lines: [getSpacingGuide('x', { ...movingBox, x: leftTargetLeft }, a.x, { ...movingBox, x: leftTargetLeft })],
            });
          }

          const betweenSpace = b.x - (a.x + a.width) - movingBox.width;
          if (betweenSpace >= 0) {
            const betweenTargetLeft = a.x + a.width + betweenSpace / 2;
            const betweenDiff = betweenTargetLeft - movingBox.x;
            if (Math.abs(betweenDiff) <= threshold) {
              candidates.x.push({
                diff: betweenDiff,
                distance: Math.abs(betweenDiff) + 0.2,
                lines: [
                  getSpacingGuide('x', a, betweenTargetLeft, { ...movingBox, x: betweenTargetLeft }),
                  getSpacingGuide('x', { ...movingBox, x: betweenTargetLeft }, b.x, { ...movingBox, x: betweenTargetLeft }),
                ],
              });
            }
          }
        } else {
          const bottomTargetTop = b.y + b.height + gap;
          const bottomDiff = bottomTargetTop - movingBox.y;
          if (Math.abs(bottomDiff) <= threshold) {
            candidates.y.push({
              diff: bottomDiff,
              distance: Math.abs(bottomDiff) + 0.2,
              lines: [getSpacingGuide('y', b, bottomTargetTop, { ...movingBox, y: bottomTargetTop })],
            });
          }

          const topTargetTop = a.y - gap - movingBox.height;
          const topDiff = topTargetTop - movingBox.y;
          if (Math.abs(topDiff) <= threshold) {
            candidates.y.push({
              diff: topDiff,
              distance: Math.abs(topDiff) + 0.2,
              lines: [getSpacingGuide('y', { ...movingBox, y: topTargetTop }, a.y, { ...movingBox, y: topTargetTop })],
            });
          }

          const betweenSpace = b.y - (a.y + a.height) - movingBox.height;
          if (betweenSpace >= 0) {
            const betweenTargetTop = a.y + a.height + betweenSpace / 2;
            const betweenDiff = betweenTargetTop - movingBox.y;
            if (Math.abs(betweenDiff) <= threshold) {
              candidates.y.push({
                diff: betweenDiff,
                distance: Math.abs(betweenDiff) + 0.2,
                lines: [
                  getSpacingGuide('y', a, betweenTargetTop, { ...movingBox, y: betweenTargetTop }),
                  getSpacingGuide('y', { ...movingBox, y: betweenTargetTop }, b.y, { ...movingBox, y: betweenTargetTop }),
                ],
              });
            }
          }
        }
      });
    }
  }
}

function findBestSnap(draggedElements, otherElements, scale) {
  const threshold = getSnapThreshold(scale);
  const candidates = { x: [], y: [] };

  draggedElements.forEach(element => {
    const draggedAnchors = getSnapAnchors(element);
    otherElements.forEach(other => {
      const otherAnchors = getSnapAnchors(other);

      draggedAnchors.x.forEach(source => {
        otherAnchors.x.forEach(target => {
          const diff = target.value - source.value;
          if (Math.abs(diff) <= threshold) {
            candidates.x.push({ diff, distance: Math.abs(diff), lines: [{ axis: 'x', value: target.value, box: otherAnchors.box }] });
          }
        });
      });

      draggedAnchors.y.forEach(source => {
        otherAnchors.y.forEach(target => {
          const diff = target.value - source.value;
          if (Math.abs(diff) <= threshold) {
            candidates.y.push({ diff, distance: Math.abs(diff), lines: [{ axis: 'y', value: target.value, box: otherAnchors.box }] });
          }
        });
      });
    });
  });

  addEqualSpacingCandidates('x', draggedElements, otherElements, threshold, candidates);
  addEqualSpacingCandidates('y', draggedElements, otherElements, threshold, candidates);

  const bestX = candidates.x.sort((a, b) => a.distance - b.distance)[0] || null;
  const bestY = candidates.y.sort((a, b) => a.distance - b.distance)[0] || null;
  return {
    deltaX: bestX ? bestX.diff : 0,
    deltaY: bestY ? bestY.diff : 0,
    lines: [
      ...(bestX ? bestX.lines : []),
      ...(bestY ? bestY.lines : []),
    ],
  };
}

function escapeSvg(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function getPrintGeometry(stage, rotation) {
  const width = Number(stage.width) || DEFAULT_STAGE.width;
  const height = Number(stage.height) || DEFAULT_STAGE.height;
  const angle = ((Number(rotation) || 0) % 360 + 360) % 360;
  if (angle === 90) return { width: height, height: width, transform: `translate(${height} 0) rotate(90)` };
  if (angle === 180) return { width, height, transform: `translate(${width} ${height}) rotate(180)` };
  if (angle === 270) return { width: height, height: width, transform: `translate(0 ${width}) rotate(270)` };
  return { width, height, transform: '' };
}

function outputPointToLayout(point, stage, rotation) {
  const width = Number(stage.width) || DEFAULT_STAGE.width;
  const height = Number(stage.height) || DEFAULT_STAGE.height;
  const angle = ((Number(rotation) || 0) % 360 + 360) % 360;
  if (angle === 90) return { x: clamp(point.y, 0, width), y: clamp(height - point.x, 0, height) };
  if (angle === 180) return { x: clamp(width - point.x, 0, width), y: clamp(height - point.y, 0, height) };
  if (angle === 270) return { x: clamp(width - point.y, 0, width), y: clamp(point.x, 0, height) };
  return { x: clamp(point.x, 0, width), y: clamp(point.y, 0, height) };
}

function getSvgPointFromEvent(event, svgRef) {
  const svg = svgRef.current;
  if (!svg) return null;
  const point = svg.createSVGPoint();
  point.x = event.clientX;
  point.y = event.clientY;
  const transformed = point.matrixTransform(svg.getScreenCTM().inverse());
  return { x: transformed.x, y: transformed.y };
}

function getPrintRectPatternMarkup(element, width, height) {
  const pattern = normalizeRectPattern(element.pattern);
  if (pattern === 'none') return '';
  const color = escapeSvg(element.patternColor || '#94a3b8');
  const spacing = clamp(Number(element.patternSpacing) || 18, 6, 80);
  const strokeWidth = clamp(Number(element.patternStrokeWidth) || 1, 1, 8);
  const parts = [];

  if (pattern === 'vertical' || pattern === 'grid') {
    for (let x = spacing; x < width; x += spacing) {
      parts.push(`<line x1="${x}" y1="0" x2="${x}" y2="${height}" stroke="${color}" stroke-width="${strokeWidth}" />`);
    }
  }
  if (pattern === 'horizontal' || pattern === 'grid') {
    for (let y = spacing; y < height; y += spacing) {
      parts.push(`<line x1="0" y1="${y}" x2="${width}" y2="${y}" stroke="${color}" stroke-width="${strokeWidth}" />`);
    }
  }
  const addDiagonal = direction => {
    const start = direction === 1 ? 0 : -width;
    const end = direction === 1 ? width + height : height;
    for (let constant = start; constant <= end; constant += spacing) {
      const points = getClippedDiagonalPatternPoints(width, height, constant, direction);
      if (points) {
        parts.push(`<line x1="${points[0]}" y1="${points[1]}" x2="${points[2]}" y2="${points[3]}" stroke="${color}" stroke-width="${strokeWidth}" />`);
      }
    }
  };
  if (pattern === 'diagonal') addDiagonal(1);
  if (pattern === 'crosshatch') {
    addDiagonal(1);
    addDiagonal(-1);
  }
  if (pattern === 'dots') {
    const radius = clamp(spacing * 0.14, 1.5, 4);
    for (let y = spacing / 2; y < height; y += spacing) {
      for (let x = spacing / 2; x < width; x += spacing) {
        parts.push(`<circle cx="${x}" cy="${y}" r="${radius}" fill="${color}" />`);
      }
    }
  }
  return parts.join('');
}

function getPrintTextMarkup(text, x, y, options = {}) {
  const lines = String(text || '').split('\n');
  const fontSize = options.fontSize || 18;
  const fill = escapeSvg(options.fill || '#111827');
  const anchor = options.anchor || 'middle';
  const weight = options.weight || 800;
  const lineHeight = fontSize * 1.18;
  const startY = y - ((lines.length - 1) * lineHeight) / 2;
  return `<text x="${x}" y="${startY}" text-anchor="${anchor}" dominant-baseline="middle" font-family="Arial, sans-serif" font-size="${fontSize}" font-weight="${weight}" fill="${fill}">${lines.map((line, index) => (
    `<tspan x="${x}" dy="${index === 0 ? 0 : lineHeight}">${escapeSvg(line)}</tspan>`
  )).join('')}</text>`;
}

function getPrintElementMarkup(element) {
  const x = Number(element.x) || 0;
  const y = Number(element.y) || 0;
  const rotation = Number(element.rotation) || 0;
  const transform = `translate(${x} ${y})${rotation ? ` rotate(${rotation})` : ''}`;

  if (element.type === 'line') {
    const points = element.points || [0, 0, 180, 0];
    return `<g transform="${transform}"><line x1="${Number(points[0]) || 0}" y1="${Number(points[1]) || 0}" x2="${Number(points[2]) || 0}" y2="${Number(points[3]) || 0}" stroke="${escapeSvg(element.stroke || '#111827')}" stroke-width="3" stroke-linecap="round" /></g>`;
  }

  if (element.type === 'text') {
    const metrics = getTextMetrics(element);
    return `<g transform="${transform}">${getPrintTextMarkup(getTextElementText(element), metrics.width / 2, metrics.height / 2, {
      fontSize: metrics.fontSize,
      fill: element.color || '#111827',
      weight: 800,
    })}</g>`;
  }

  if (element.type === 'gate' || element.type === 'loadingBay' || element.type === 'rect') {
    const width = Math.max(1, Number(element.width) || (element.type === 'rect' ? 180 : 120));
    const height = Math.max(1, Number(element.height) || (element.type === 'rect' ? 90 : 56));
    const fill = element.color || (element.type === 'loadingBay' ? '#f97316' : element.type === 'gate' ? '#039eff' : '#334155');
    const text = element.type === 'rect' ? getRectText(element) : (element.label || (element.type === 'loadingBay' ? 'Ladezone' : 'Tor'));
    const patternMarkup = element.type === 'rect' ? getPrintRectPatternMarkup(element, width, height) : '';
    return `<g transform="${transform}"><rect x="0" y="0" width="${width}" height="${height}" rx="${element.type === 'rect' ? 4 : 6}" fill="${escapeSvg(fill)}" stroke="rgba(15,23,42,0.45)" stroke-width="2" />${patternMarkup}${text ? getPrintTextMarkup(text, width / 2, height / 2, {
      fontSize: Math.max(8, Number(element.fontSize) || Math.min(20, height * 0.34)),
      fill: element.textColor || '#ffffff',
      weight: 900,
    }) : ''}</g>`;
  }

  return '';
}

function buildPrintSvgMarkup({ layout, floor, rotation, backgroundColor, point }) {
  const stage = layout.stage || DEFAULT_STAGE;
  const geometry = getPrintGeometry(stage, rotation);
  const elements = (layout.elements || [])
    .filter(element => isElementVisibleOnFloor(element, floor))
    .map(getPrintElementMarkup)
    .join('');
  const pointMarkup = point
    ? `<circle cx="${point.x}" cy="${point.y}" r="22" fill="none" stroke="#dc2626" stroke-width="8" /><circle cx="${point.x}" cy="${point.y}" r="8" fill="#dc2626" />`
    : '';
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${geometry.width} ${geometry.height}" width="100%" height="100%" role="img"><rect width="${geometry.width}" height="${geometry.height}" fill="${escapeSvg(backgroundColor || '#ffffff')}" /><g${geometry.transform ? ` transform="${geometry.transform}"` : ''}>${elements}${pointMarkup}</g></svg>`;
}

function PrintSvgPreview({ layout, floor, rotation, backgroundColor, point, settingPoint, onSetPoint }) {
  const svgRef = useRef(null);
  const stage = layout.stage || DEFAULT_STAGE;
  const geometry = getPrintGeometry(stage, rotation);
  const elements = (layout.elements || []).filter(element => isElementVisibleOnFloor(element, floor));

  const handleClick = event => {
    if (!settingPoint) return;
    const outputPoint = getSvgPointFromEvent(event, svgRef);
    if (!outputPoint) return;
    onSetPoint(outputPointToLayout(outputPoint, stage, rotation));
  };

  return (
    <svg
      ref={svgRef}
      style={{ ...styles.printPreviewSvg, cursor: settingPoint ? 'crosshair' : 'default' }}
      viewBox={`0 0 ${geometry.width} ${geometry.height}`}
      onClick={handleClick}
    >
      <rect width={geometry.width} height={geometry.height} fill={backgroundColor || '#ffffff'} />
      <g transform={geometry.transform}>
        {elements.map(element => (
          <PrintSvgElement key={element.id} element={element} />
        ))}
        {point && (
          <>
            <circle cx={point.x} cy={point.y} r="22" fill="none" stroke="#dc2626" strokeWidth="8" />
            <circle cx={point.x} cy={point.y} r="8" fill="#dc2626" />
          </>
        )}
      </g>
    </svg>
  );
}

function PrintSvgElement({ element }) {
  const x = Number(element.x) || 0;
  const y = Number(element.y) || 0;
  const rotation = Number(element.rotation) || 0;
  const transform = `translate(${x} ${y})${rotation ? ` rotate(${rotation})` : ''}`;

  if (element.type === 'line') {
    const points = element.points || [0, 0, 180, 0];
    return (
      <g transform={transform}>
        <line x1={Number(points[0]) || 0} y1={Number(points[1]) || 0} x2={Number(points[2]) || 0} y2={Number(points[3]) || 0} stroke={element.stroke || '#111827'} strokeWidth="3" strokeLinecap="round" />
      </g>
    );
  }

  if (element.type === 'text') {
    const metrics = getTextMetrics(element);
    return (
      <g transform={transform}>
        <PrintSvgText text={getTextElementText(element)} x={metrics.width / 2} y={metrics.height / 2} fontSize={metrics.fontSize} fill={element.color || '#111827'} />
      </g>
    );
  }

  if (element.type === 'gate' || element.type === 'loadingBay' || element.type === 'rect') {
    const width = Math.max(1, Number(element.width) || (element.type === 'rect' ? 180 : 120));
    const height = Math.max(1, Number(element.height) || (element.type === 'rect' ? 90 : 56));
    const fill = element.color || (element.type === 'loadingBay' ? '#f97316' : element.type === 'gate' ? '#039eff' : '#334155');
    const text = element.type === 'rect' ? getRectText(element) : (element.label || (element.type === 'loadingBay' ? 'Ladezone' : 'Tor'));
    return (
      <g transform={transform}>
        <rect width={width} height={height} rx={element.type === 'rect' ? 4 : 6} fill={fill} stroke="rgba(15,23,42,0.45)" strokeWidth="2" />
        {text && <PrintSvgText text={text} x={width / 2} y={height / 2} fontSize={Math.max(8, Number(element.fontSize) || Math.min(20, height * 0.34))} fill={element.textColor || '#ffffff'} />}
      </g>
    );
  }

  return null;
}

function PrintSvgText({ text, x, y, fontSize, fill }) {
  const lines = String(text || '').split('\n');
  const lineHeight = fontSize * 1.18;
  const startY = y - ((lines.length - 1) * lineHeight) / 2;
  return (
    <text x={x} y={startY} textAnchor="middle" dominantBaseline="middle" fontFamily="Arial, sans-serif" fontSize={fontSize} fontWeight="900" fill={fill}>
      {lines.map((line, index) => (
        <tspan key={index} x={x} dy={index === 0 ? 0 : lineHeight}>{line}</tspan>
      ))}
    </text>
  );
}

const styles = {
  page: { display: 'grid', gridTemplateColumns: '170px minmax(0, 1fr) 280px', gap: '16px', alignItems: 'start' },
  panel: { ...theme.glassCard, color: 'var(--board-text-strong, #fff)', padding: '18px', boxSizing: 'border-box' },
  canvasPanel: { ...theme.glassCard, color: 'var(--board-text-strong, #fff)', padding: '12px', boxSizing: 'border-box', overflow: 'auto' },
  title: { margin: '0 0 14px', fontSize: '18px', fontWeight: 900 },
  groupTitle: { margin: '18px 0 8px', fontSize: '12px', color: 'var(--board-text-muted, rgba(255,255,255,0.58))', fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0 },
  button: { ...theme.button, ...theme.mutedButton, width: '100%', marginBottom: '8px', boxShadow: 'none', borderRadius: '6px' },
  primaryButton: { ...theme.button, ...theme.primaryButton, width: '100%', borderRadius: '6px' },
  dangerButton: { ...theme.button, ...theme.dangerButton, width: '100%', borderRadius: '6px', marginTop: '8px' },
  input: { ...theme.input, width: '100%', marginBottom: '10px' },
  select: { ...theme.select, width: '100%', marginBottom: '10px', colorScheme: 'var(--board-color-scheme, dark)' },
  option: { backgroundColor: 'var(--board-surface, #161322)', color: 'var(--board-text-strong, #fff)' },
  label: { display: 'block', marginBottom: '5px', fontSize: '12px', color: 'var(--board-text-muted, rgba(255,255,255,0.62))' },
  checkboxLabel: { display: 'flex', alignItems: 'center', gap: '8px', margin: '8px 0 10px', color: 'var(--board-text-soft, rgba(255,255,255,0.78))', fontSize: '13px', lineHeight: 1.35 },
  checkbox: { width: '16px', height: '16px', accentColor: 'var(--board-accent, #039eff)', flex: '0 0 auto' },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' },
  layerRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' },
  floorTabs: { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px', marginBottom: '12px' },
  floorButton: { ...theme.button, ...theme.mutedButton, borderRadius: '6px', boxShadow: 'none', padding: '9px 10px' },
  floorButtonActive: { ...theme.button, ...theme.primaryButton, borderRadius: '6px', padding: '9px 10px' },
  tinyButton: { ...theme.button, ...theme.mutedButton, width: '100%', marginBottom: 0, boxShadow: 'none', borderRadius: '6px', padding: '8px 6px', fontSize: '12px' },
  stageShell: { display: 'inline-block', background: 'var(--board-surface-soft, #050814)', border: '1px solid var(--board-border, rgba(255,255,255,0.18))', touchAction: 'none' },
  status: { minHeight: '18px', marginTop: '10px', color: 'var(--board-text-soft, rgba(255,255,255,0.68))', fontSize: '12px' },
  autosave: { minHeight: '16px', margin: '-2px 0 8px', color: 'var(--board-text-soft, rgba(255,255,255,0.68))', fontSize: '11px', lineHeight: 1.25 },
  muted: { color: 'var(--board-text-muted, rgba(255,255,255,0.58))', fontSize: '13px', lineHeight: 1.35 },
  selectionSummary: { color: 'var(--board-text-strong, #fff)', fontSize: '15px', fontWeight: 800, margin: '0 0 12px' },
  conflictBanner: { ...theme.glassCard, marginBottom: '14px', padding: '12px 14px', border: '1px solid rgba(250,204,21,0.42)', background: 'rgba(250,204,21,0.12)', color: 'var(--board-text-strong, #fff)', display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' },
  conflictText: { margin: 0, color: 'var(--board-text-soft, rgba(255,255,255,0.78))', fontSize: '13px', lineHeight: 1.35 },
  updateToast: { position: 'fixed', top: '18px', right: '18px', zIndex: 1200, width: 'min(360px, calc(100vw - 32px))', padding: '14px 16px', borderRadius: '8px', border: '1px solid rgba(250,204,21,0.62)', background: 'rgba(24,18,6,0.96)', color: '#fef3c7', boxShadow: '0 18px 42px rgba(0,0,0,0.34)', display: 'grid', gap: '4px', fontSize: '13px', lineHeight: 1.35 },
  updateToastTitle: { fontWeight: 900, color: '#fff7ed' },
  modalBackdrop: { position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(2,6,23,0.72)', display: 'grid', placeItems: 'center', padding: '24px' },
  printModal: { ...theme.glassCard, width: 'min(1180px, 96vw)', maxHeight: '92vh', display: 'grid', gridTemplateColumns: '280px minmax(0, 1fr)', gap: '16px', padding: '16px', boxSizing: 'border-box', color: 'var(--board-text-strong, #fff)' },
  printControls: { display: 'grid', alignContent: 'start', gap: '10px' },
  printPreviewWrap: { minWidth: 0, minHeight: 'min(68vh, 720px)', border: '1px solid var(--board-border, rgba(255,255,255,0.18))', borderRadius: '8px', overflow: 'hidden', background: '#111827', display: 'grid', placeItems: 'center' },
  printPreviewSvg: { width: '100%', height: '100%', display: 'block' },
  modalActions: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '8px' },
};

function LabeledInput({ label, type = 'text', value, onChange, min, step }) {
  return (
    <label>
      <span style={styles.label}>{label}</span>
      <input
        style={styles.input}
        type={type}
        value={value ?? ''}
        min={min}
        step={step}
        onChange={e => onChange(type === 'number' ? toNumber(e.target.value) : e.target.value)}
      />
    </label>
  );
}

function LabeledCheckbox({ label, checked, indeterminate = false, onChange }) {
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <label style={styles.checkboxLabel}>
      <input
        ref={inputRef}
        style={styles.checkbox}
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

function FloorSelector({ activeFloor, onChange }) {
  return (
    <div style={styles.floorTabs}>
      {FLOORS.map(floor => (
        <button
          key={floor.id}
          style={activeFloor === floor.id ? styles.floorButtonActive : styles.floorButton}
          type="button"
          title={floor.label}
          aria-label={floor.label}
          onClick={() => onChange(floor.id)}
        >
          {floor.id}
        </button>
      ))}
    </div>
  );
}

function ElementToolbar({ onAdd, onSave, onReload, onDownload, onImportClick, onPrintClick, onSelectType, saving, saveDisabled, importing, features, activeFloor, onFloorChange, autosaveLabel }) {
  return (
    <aside style={styles.panel}>
      <h2 style={styles.title}>Elemente</h2>
      <FloorSelector activeFloor={activeFloor} onChange={onFloorChange} />
      <button style={styles.button} type="button" onClick={() => onAdd('gate')}>Gate</button>
      <button style={styles.button} type="button" onClick={() => onAdd('loadingBay')}>Ladezone</button>
      <button style={styles.button} type="button" onClick={() => onAdd('rect')}>Rechteck</button>
      <button style={styles.button} type="button" onClick={() => onAdd('line')}>Linie</button>
      <button style={styles.button} type="button" onClick={() => onAdd('text')}>Text</button>

      <div style={styles.groupTitle}>Layout</div>
      <button style={styles.primaryButton} type="button" onClick={onSave} disabled={saving || saveDisabled}>
        {saving ? 'Speichern...' : 'Speichern'}
      </button>
      <div style={styles.autosave}>{autosaveLabel}</div>
      <button style={{ ...styles.button, marginTop: '8px' }} type="button" onClick={onReload}>Neu laden</button>
      <button style={styles.button} type="button" onClick={onDownload} disabled={!features.layoutDownload}>Download</button>
      <button style={styles.button} type="button" onClick={onImportClick} disabled={!features.layoutUpload || importing || saving || saveDisabled}>
        {importing ? 'Importiere...' : 'Upload'}
      </button>
      <button style={styles.button} type="button" onClick={onPrintClick}>Drucken</button>

      <div style={styles.groupTitle}>Auswahl</div>
      <button style={styles.button} type="button" onClick={() => onSelectType('line')}>Alle Linien auswaehlen</button>
      <button style={styles.button} type="button" onClick={() => onSelectType('rect')}>Alle Rechtecke auswaehlen</button>
      <button style={styles.button} type="button" onClick={() => onSelectType('gate')}>Alle Tore auswaehlen</button>
      <button style={styles.button} type="button" onClick={() => onSelectType('loadingBay')}>Alle Ladezonen auswaehlen</button>
      <button style={styles.button} type="button" onClick={() => onSelectType('text')}>Alle Texte auswaehlen</button>
    </aside>
  );
}

function ElementPropertiesPanel({ selected, selectedElements, selectedCount, gates, loadingBays, onUpdate, onUpdateMany, onDelete, onDeleteSelected, onStageUpdate, stage, onMoveLayer }) {
  const selectedLines = selectedElements.filter(element => element.type === 'line');
  const allFloorsSelected = selectedElements.length > 0 && selectedElements.every(element => element.allFloors === true);
  const someFloorsSelected = selectedElements.some(element => element.allFloors === true);
  const patchSelectedAllFloors = allFloors => {
    onUpdateMany(Object.fromEntries(selectedElements.map(element => [
      element.id,
      { allFloors },
    ])));
  };
  const selectedLineLengths = selectedLines.map(getLineLength);
  const sharedLineLength = selectedLineLengths.length > 0 && selectedLineLengths.every(length => Math.round(length) === Math.round(selectedLineLengths[0]))
    ? Math.round(selectedLineLengths[0])
    : '';
  const patchSelectedLinesLength = length => {
    const nextLength = Math.max(1, Number(length) || 1);
    onUpdateMany(Object.fromEntries(selectedLines.map(element => [
      element.id,
      { points: setLineLength(element, nextLength) },
    ])));
  };

  if (!selected) {
    return (
      <aside style={styles.panel}>
        <h2 style={styles.title}>Eigenschaften</h2>
        {selectedCount > 1 ? (
          <>
            <p style={styles.selectionSummary}>{selectedCount} Elemente ausgewählt</p>
            <p style={styles.muted}>Die Auswahl kann gemeinsam verschoben werden.</p>
            <LabeledCheckbox
              label="Auf allen Ebenen behalten"
              checked={allFloorsSelected}
              indeterminate={someFloorsSelected && !allFloorsSelected}
              onChange={patchSelectedAllFloors}
            />
            {selectedLines.length > 0 && (
              <>
                <div style={styles.groupTitle}>Linien</div>
                <LabeledInput
                  label={selectedLines.length === selectedCount ? 'Laenge' : `Laenge (${selectedLines.length} Linien)`}
                  type="number"
                  value={sharedLineLength}
                  min={1}
                  onChange={patchSelectedLinesLength}
                />
              </>
            )}
            <button style={styles.dangerButton} type="button" onClick={onDeleteSelected}>Auswahl löschen</button>
          </>
        ) : (
          <p style={styles.muted}>Wähle ein Element aus, um Position, Größe, Label und Verknüpfungen zu bearbeiten.</p>
        )}
        <div style={styles.groupTitle}>Stage</div>
        <div style={styles.row}>
          <LabeledInput label="Breite" type="number" value={stage.width} min={600} onChange={width => onStageUpdate({ width })} />
          <LabeledInput label="Höhe" type="number" value={stage.height} min={400} onChange={height => onStageUpdate({ height })} />
        </div>
      </aside>
    );
  }

  const patchSelected = patch => onUpdate(selected.id, patch);
  const points = selected.points || [0, 0, 120, 0];

  return (
    <aside style={styles.panel}>
      <h2 style={styles.title}>{ELEMENT_LABELS[selected.type] || selected.type}</h2>

      <LabeledInput label="Label" value={selected.label || ''} onChange={label => patchSelected({ label })} />

      <div style={styles.groupTitle}>Reihenfolge</div>
      <div style={styles.layerRow}>
        <button style={styles.tinyButton} type="button" onClick={() => onMoveLayer(selected.id, 'back')}>Eine runter</button>
        <button style={styles.tinyButton} type="button" onClick={() => onMoveLayer(selected.id, 'bottom')}>Ganz runter</button>
        <button style={styles.tinyButton} type="button" onClick={() => onMoveLayer(selected.id, 'forward')}>Eine hoch</button>
        <button style={styles.tinyButton} type="button" onClick={() => onMoveLayer(selected.id, 'top')}>Ganz hoch</button>
      </div>

      <div style={styles.row}>
        <LabeledInput label="X" type="number" value={Math.round(selected.x || 0)} onChange={x => patchSelected({ x })} />
        <LabeledInput label="Y" type="number" value={Math.round(selected.y || 0)} onChange={y => patchSelected({ y })} />
      </div>

      {selected.type !== 'line' && (
        <div style={styles.row}>
          <LabeledInput label="Breite" type="number" value={Math.round(selected.width || 1)} min={10} onChange={width => patchSelected({ width })} />
          <LabeledInput label="Höhe" type="number" value={Math.round(selected.height || 1)} min={10} onChange={height => patchSelected({ height })} />
        </div>
      )}

      <LabeledInput label="Rotation" type="number" value={normalizeRotation(selected.rotation || 0)} onChange={rotation => patchSelected({ rotation: normalizeRotation(rotation) })} />

      <LabeledCheckbox
        label="Auf allen Ebenen behalten"
        checked={selected.allFloors === true}
        onChange={allFloors => patchSelected({ allFloors })}
      />

      {selected.type === 'gate' && (
        <label>
          <span style={styles.label}>Verknuepftes Tor</span>
          <select
            style={styles.select}
            value={selected.gateId || ''}
            onChange={e => {
              const gate = gates.find(item => String(item.id) === e.target.value);
              patchSelected({ gateId: e.target.value ? Number(e.target.value) : '', label: gate?.name || selected.label });
            }}
          >
            <option style={styles.option} value="">- kein Tor -</option>
            {gates.map(gate => (
              <option style={styles.option} key={gate.id} value={gate.id}>{gate.name}</option>
            ))}
          </select>
        </label>
      )}

      {selected.type === 'loadingBay' && (
        <label>
          <span style={styles.label}>Verknuepfte Ladezone</span>
          <select
            style={styles.select}
            value={selected.loadingBayId || ''}
            onChange={e => {
              const loadingBay = loadingBays.find(item => String(item.id) === e.target.value);
              patchSelected({ loadingBayId: e.target.value ? Number(e.target.value) : '', label: loadingBay?.name || selected.label });
            }}
          >
            <option style={styles.option} value="">- keine Ladezone -</option>
            {loadingBays.map(loadingBay => (
              <option style={styles.option} key={loadingBay.id} value={loadingBay.id}>{loadingBay.name}</option>
            ))}
          </select>
        </label>
      )}

      {(selected.type === 'text' || selected.type === 'rect') && (
        <>
          <LabeledInput label="Text" value={selected.text || ''} onChange={text => patchSelected({ text, label: selected.type === 'text' ? (text || selected.label) : selected.label })} />
          <LabeledInput label="Schriftgroesse" type="number" value={selected.fontSize || (selected.type === 'rect' ? 18 : 28)} min={8} onChange={fontSize => patchSelected({ fontSize })} />
        </>
      )}

      {selected.type === 'line' && (
        <>
          <LabeledInput label="Laenge" type="number" min={1} value={Math.round(getLineLength(selected))} onChange={length => patchSelected({ points: setLineLength(selected, length) })} />
          <div style={styles.groupTitle}>Linienpunkte</div>
          <div style={styles.row}>
            <LabeledInput label="X1" type="number" value={points[0]} onChange={value => patchSelected({ points: [value, points[1], points[2], points[3]] })} />
            <LabeledInput label="Y1" type="number" value={points[1]} onChange={value => patchSelected({ points: [points[0], value, points[2], points[3]] })} />
            <LabeledInput label="X2" type="number" value={points[2]} onChange={value => patchSelected({ points: [points[0], points[1], value, points[3]] })} />
            <LabeledInput label="Y2" type="number" value={points[3]} onChange={value => patchSelected({ points: [points[0], points[1], points[2], value] })} />
          </div>
        </>
      )}

      {(selected.type === 'gate' || selected.type === 'loadingBay' || selected.type === 'rect') && (
        <LabeledInput label="Füllfarbe" type="color" value={selected.color || '#334155'} onChange={color => patchSelected({ color })} />
      )}

      {selected.type === 'text' && (
        <LabeledInput label="Textfarbe" type="color" value={selected.color || '#f8fafc'} onChange={color => patchSelected({ color })} />
      )}

      {selected.type === 'rect' && (
        <>
          <LabeledInput label="Textfarbe" type="color" value={selected.textColor || '#fff'} onChange={textColor => patchSelected({ textColor })} />
          <label>
            <span style={styles.label}>Muster</span>
            <select
              style={styles.select}
              value={normalizeRectPattern(selected.pattern)}
              onChange={e => patchSelected({ pattern: e.target.value })}
            >
              {RECT_PATTERN_OPTIONS.map(option => (
                <option style={styles.option} key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          {normalizeRectPattern(selected.pattern) !== 'none' && (
            <div style={styles.row}>
              <LabeledInput label="Musterfarbe" type="color" value={selected.patternColor || '#94a3b8'} onChange={patternColor => patchSelected({ patternColor })} />
              <LabeledInput label="Abstand" type="number" value={selected.patternSpacing || 18} min={6} onChange={patternSpacing => patchSelected({ patternSpacing })} />
            </div>
          )}
        </>
      )}

      {selected.type === 'line' && (
        <LabeledInput label="Linienfarbe" type="color" value={selected.stroke || '#f8fafc'} onChange={stroke => patchSelected({ stroke })} />
      )}

      <button style={styles.dangerButton} type="button" onClick={() => onDelete(selected.id)}>Löschen</button>
    </aside>
  );
}

function WarehousePrintModal({ layout, activeFloor, onClose }) {
  const [rotation, setRotation] = useState(0);
  const [floor, setFloor] = useState(activeFloor);
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [point, setPoint] = useState(null);
  const [settingPoint, setSettingPoint] = useState(false);

  const openPrintDialog = () => {
    const svg = buildPrintSvgMarkup({ layout, floor, rotation, backgroundColor, point });
    const geometry = getPrintGeometry(layout.stage || DEFAULT_STAGE, rotation);
    const pageOrientation = geometry.width >= geometry.height ? 'landscape' : 'portrait';
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      window.alert('Druckfenster konnte nicht geoeffnet werden. Bitte Popups fuer diese Seite erlauben.');
      return;
    }
    printWindow.document.write(`<!doctype html><html><head><title>Lageplan Ebene ${floor}</title><style>
      @page { size: A4 ${pageOrientation}; margin: 8mm; }
      html, body { margin: 0; width: 100%; height: 100%; background: ${escapeSvg(backgroundColor)}; }
      body { display: grid; place-items: center; }
      svg { width: 100vw; height: 100vh; object-fit: contain; }
    </style></head><body>${svg}<script>window.onload = function(){ window.focus(); window.print(); };</script></body></html>`);
    printWindow.document.close();
  };

  return (
    <div style={styles.modalBackdrop} role="dialog" aria-modal="true">
      <section style={styles.printModal}>
        <aside style={styles.printControls}>
          <h2 style={styles.title}>Lageplan drucken</h2>
          <label>
            <span style={styles.label}>Orientierung</span>
            <select style={styles.select} value={rotation} onChange={event => setRotation(Number(event.target.value))}>
              {[0, 90, 180, 270].map(value => (
                <option style={styles.option} key={value} value={value}>{value} Grad</option>
              ))}
            </select>
          </label>
          <label>
            <span style={styles.label}>Ebene</span>
            <select style={styles.select} value={floor} onChange={event => setFloor(normalizeFloor(event.target.value))}>
              {FLOORS.map(item => (
                <option style={styles.option} key={item.id} value={item.id}>{item.label}</option>
              ))}
            </select>
          </label>
          <label>
            <span style={styles.label}>Hintergrundfarbe</span>
            <input style={styles.input} type="color" value={backgroundColor} onChange={event => setBackgroundColor(event.target.value)} />
          </label>
          <button style={settingPoint ? styles.primaryButton : styles.button} type="button" onClick={() => setSettingPoint(value => !value)}>
            {settingPoint ? 'Punkt in Vorschau anklicken' : 'Aktuellen Punkt setzen'}
          </button>
          <button style={styles.button} type="button" onClick={() => {
            setPoint(null);
            setSettingPoint(false);
          }}>
            Punkt entfernen
          </button>
          <div style={styles.modalActions}>
            <button style={styles.button} type="button" onClick={onClose}>Schliessen</button>
            <button style={styles.primaryButton} type="button" onClick={openPrintDialog}>Drucken</button>
          </div>
        </aside>
        <div style={styles.printPreviewWrap}>
          <PrintSvgPreview
            layout={layout}
            floor={floor}
            rotation={rotation}
            backgroundColor={backgroundColor}
            point={point}
            settingPoint={settingPoint}
            onSetPoint={nextPoint => {
              setPoint(nextPoint);
              setSettingPoint(false);
            }}
          />
        </div>
      </section>
    </div>
  );
}

function LayoutCanvas({ layout, selectedIds, activeFloor, onSelect, onSelectMany, onUpdate, onUpdateMany, onViewportChange, onEditorActivity }) {
  const canvasPanelRef = useRef(null);
  const stageShellRef = useRef(null);
  const transformerRef = useRef(null);
  const viewportGroupRef = useRef(null);
  const shapeRefs = useRef({});
  const selectionStartRef = useRef(null);
  const panStartRef = useRef(null);
  const groupDragRef = useRef(null);
  const groupTransformRef = useRef(null);
  const lineEndpointDragRef = useRef(null);
  const rotateIndividuallyRef = useRef(false);
  const [selectionBox, setSelectionBox] = useState(null);
  const [snapLines, setSnapLines] = useState([]);
  const [view, setView] = useState({ x: 0, y: 0, scale: 1 });
  const [isPanning, setIsPanning] = useState(false);
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const visibleElements = useMemo(
    () => layout.elements.filter(element => isElementVisibleOnFloor(element, activeFloor)),
    [activeFloor, layout.elements]
  );

  const reportViewportCenter = useCallback(() => {
    const panel = canvasPanelRef.current;
    const shell = stageShellRef.current;
    let screenX = layout.stage.width / 2;
    let screenY = layout.stage.height / 2;

    if (panel && shell) {
      const panelRect = panel.getBoundingClientRect();
      const shellRect = shell.getBoundingClientRect();
      const visibleLeft = Math.max(panelRect.left, shellRect.left);
      const visibleRight = Math.min(panelRect.right, shellRect.right);
      const visibleTop = Math.max(panelRect.top, shellRect.top);
      const visibleBottom = Math.min(panelRect.bottom, shellRect.bottom);

      if (visibleRight > visibleLeft) {
        screenX = (visibleLeft + visibleRight) / 2 - shellRect.left;
      } else {
        screenX = panel.scrollLeft + panel.clientWidth / 2 - shell.offsetLeft;
      }

      if (visibleBottom > visibleTop) {
        screenY = (visibleTop + visibleBottom) / 2 - shellRect.top;
      } else {
        screenY = panel.scrollTop + panel.clientHeight / 2 - shell.offsetTop;
      }
    }

    const center = {
      x: clamp((screenX - view.x) / view.scale, 0, layout.stage.width),
      y: clamp((screenY - view.y) / view.scale, 0, layout.stage.height),
    };
    onViewportChange?.(center);
  }, [layout.stage.height, layout.stage.width, onViewportChange, view.scale, view.x, view.y]);

  useEffect(() => {
    reportViewportCenter();
  }, [reportViewportCenter]);

  useEffect(() => {
    const transformer = transformerRef.current;
    if (!transformer) return;
    const selectedElements = selectedIds
      .map(id => visibleElements.find(element => element.id === id))
      .filter(Boolean);
    const selectedNodes = selectedElements
      .filter(element => selectedElements.length > 1 || element.type !== 'line')
      .map(element => shapeRefs.current[element.id])
      .filter(Boolean);
    transformer.nodes(selectedNodes);
    transformer.getLayer()?.batchDraw();
  }, [selectedIds, visibleElements]);

  useEffect(() => {
    const handleModifier = event => {
      rotateIndividuallyRef.current = Boolean(event.ctrlKey || event.metaKey);
    };
    const clearModifier = () => {
      rotateIndividuallyRef.current = false;
    };

    window.addEventListener('keydown', handleModifier);
    window.addEventListener('keyup', handleModifier);
    window.addEventListener('blur', clearModifier);
    return () => {
      window.removeEventListener('keydown', handleModifier);
      window.removeEventListener('keyup', handleModifier);
      window.removeEventListener('blur', clearModifier);
    };
  }, []);

  const getLayoutPointer = stage => {
    const pointer = stage.getPointerPosition();
    const viewportGroup = viewportGroupRef.current;
    if (!pointer || !viewportGroup) return null;

    const transform = viewportGroup.getAbsoluteTransform().copy();
    transform.invert();
    return transform.point(pointer);
  };

  const getDragSnap = (element, node, event) => {
    const snapDisabled = Boolean(event?.ctrlKey || event?.metaKey);
    const selectedSet = selectedIdSet.has(element.id) ? selectedIdSet : new Set([element.id]);
    const drag = groupDragRef.current;
    const rawDelta = drag && drag.anchorId === element.id
      ? {
          x: node.x() - drag.anchorStart.x,
          y: node.y() - drag.anchorStart.y,
        }
      : {
          x: node.x() - (element.x || 0),
          y: node.y() - (element.y || 0),
        };

    const draggedElements = visibleElements
      .filter(item => selectedSet.has(item.id))
      .map(item => getDraggedElementSnapshot(item, {
        x: (drag?.positions?.[item.id]?.x ?? item.x ?? 0) + rawDelta.x,
        y: (drag?.positions?.[item.id]?.y ?? item.y ?? 0) + rawDelta.y,
      }));
    const otherElements = visibleElements.filter(item => !selectedSet.has(item.id));
    const snap = snapDisabled ? { deltaX: 0, deltaY: 0, lines: [] } : findBestSnap(draggedElements, otherElements, view.scale);

    return {
      rawDelta,
      deltaX: rawDelta.x + snap.deltaX,
      deltaY: rawDelta.y + snap.deltaY,
      lines: snap.lines,
    };
  };

  const updateDrag = (element, node, event) => {
    const snap = getDragSnap(element, node, event);
    node.position({
      x: (element.x || 0) + snap.deltaX,
      y: (element.y || 0) + snap.deltaY,
    });
    setSnapLines(snap.lines);
    onUpdate(element.id, { x: node.x(), y: node.y() });
  };

  const updateGroupDrag = (element, node, options = {}) => {
    const drag = groupDragRef.current;
    if (!drag || drag.anchorId !== element.id) return false;

    const snap = getDragSnap(element, node, options.event);
    const deltaX = snap.deltaX;
    const deltaY = snap.deltaY;
    node.position({
      x: drag.anchorStart.x + deltaX,
      y: drag.anchorStart.y + deltaY,
    });
    setSnapLines(snap.lines);

    const patches = Object.fromEntries(Object.entries(drag.positions).map(([id, position]) => [
      id,
      {
        x: position.x + deltaX,
        y: position.y + deltaY,
      },
    ]));

    onUpdateMany(patches, {
      recordHistory: options.recordHistory !== false,
      historyLayout: drag.layoutBefore,
    });
    return true;
  };

  const setStageCursor = (node, cursor) => {
    const container = node?.getStage()?.container();
    if (container) container.style.cursor = cursor;
  };

  const updateTransform = (element, node) => {
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    node.scaleX(1);
    node.scaleY(1);
    onUpdate(element.id, {
      x: node.x(),
      y: node.y(),
      rotation: normalizeRotation(node.rotation()),
      width: Math.max(10, (element.width || node.width() || 10) * scaleX),
      height: Math.max(10, (element.height || node.height() || 10) * scaleY),
    });
  };

  const getSelectedTransformElements = () => selectedIds
    .map(id => visibleElements.find(element => element.id === id))
    .filter(Boolean);

  const getNodeTransformSnapshot = (element, node) => ({
    id: element.id,
    x: node.x(),
    y: node.y(),
    rotation: normalizeRotation(node.rotation()),
    scaleX: node.scaleX(),
    scaleY: node.scaleY(),
    width: element.width,
    height: element.height,
  });

  const beginGroupTransform = () => {
    if (selectedIds.length < 2) {
      groupTransformRef.current = null;
      return;
    }

    const elements = getSelectedTransformElements()
      .map(element => {
        const node = shapeRefs.current[element.id];
        return node ? getNodeTransformSnapshot(element, node) : null;
      })
      .filter(Boolean);

    groupTransformRef.current = {
      layoutBefore: layout,
      elements,
      usedIndividualRotation: false,
      anchorId: elements[0]?.id,
    };
  };

  const applyIndividualGroupRotation = event => {
    const session = groupTransformRef.current;
    if (!session || selectedIds.length < 2 || session.elements.length === 0) return false;

    const modifierPressed = Boolean(event?.ctrlKey || event?.metaKey || rotateIndividuallyRef.current);
    if (!modifierPressed) return false;

    const anchor = session.elements.find(element => element.id === session.anchorId) || session.elements[0];
    const anchorNode = shapeRefs.current[anchor.id];
    if (!anchorNode) return false;

    const rotationDelta = getRotationDelta(anchor.rotation, normalizeRotation(anchorNode.rotation()));
    session.usedIndividualRotation = true;

    session.elements.forEach(start => {
      const node = shapeRefs.current[start.id];
      if (!node) return;
      node.position({ x: start.x, y: start.y });
      node.rotation(normalizeRotation(start.rotation + rotationDelta));
      node.scaleX(start.scaleX || 1);
      node.scaleY(start.scaleY || 1);
    });

    transformerRef.current?.forceUpdate();
    transformerRef.current?.getLayer()?.batchDraw();
    return true;
  };

  const finishGroupTransform = event => {
    const session = groupTransformRef.current;
    if (!session || selectedIds.length < 2) return false;

    applyIndividualGroupRotation(event);

    const patches = {};
    session.elements.forEach(start => {
      const node = shapeRefs.current[start.id];
      if (!node) return;
      node.scaleX(start.scaleX || 1);
      node.scaleY(start.scaleY || 1);
      patches[start.id] = {
        x: node.x(),
        y: node.y(),
        rotation: normalizeRotation(node.rotation()),
      };
    });

    groupTransformRef.current = null;
    onUpdateMany(patches, {
      recordHistory: true,
      historyLayout: session.layoutBefore,
    });
    return true;
  };

  const updateLineEndpoint = (element, endpointIndex, localPoint, options = {}) => {
    const snap = getLineEndpointSnap(
      element,
      endpointIndex,
      localPoint,
      visibleElements.filter(item => item.id !== element.id),
      view.scale,
      options.event
    );
    if (options.node) {
      options.node.position(snap.point);
    }
    const points = getLineLocalPoints(element);
    points[endpointIndex] = snap.point;
    setSnapLines(snap.lines);
    onUpdateMany({
      [element.id]: {
        points: [points[0].x, points[0].y, points[1].x, points[1].y],
      },
    }, {
      recordHistory: options.recordHistory !== false,
      historyLayout: options.historyLayout,
    });
  };

  const renderElement = element => {
    const common = {
      key: element.id,
      ref: node => {
        if (node) {
          shapeRefs.current[element.id] = node;
        } else {
          delete shapeRefs.current[element.id];
        }
      },
      x: element.x || 0,
      y: element.y || 0,
      rotation: element.rotation || 0,
      draggable: true,
      onMouseDown: e => {
        e.cancelBubble = true;
        if (e.evt.shiftKey || e.evt.ctrlKey || e.evt.metaKey) {
          onSelect(element.id, true);
        } else if (!selectedIdSet.has(element.id)) {
          onSelect(element.id, false);
        }
      },
      onClick: e => {
        e.cancelBubble = true;
        if (e.evt.shiftKey || e.evt.ctrlKey || e.evt.metaKey) return;
        onSelect(element.id, false);
      },
      onTap: e => {
        e.cancelBubble = true;
        onSelect(element.id, false);
      },
      onMouseEnter: e => setStageCursor(e.target, 'grab'),
      onMouseLeave: e => setStageCursor(e.target, 'default'),
      onDragStart: e => {
        setStageCursor(e.target, 'grabbing');
        onEditorActivity?.(true);
        if (selectedIdSet.has(element.id) && selectedIds.length > 1) {
          groupDragRef.current = {
            anchorId: element.id,
            anchorStart: { x: element.x || 0, y: element.y || 0 },
            layoutBefore: layout,
            positions: Object.fromEntries(layout.elements
              .filter(item => selectedIdSet.has(item.id))
              .map(item => [item.id, { x: item.x || 0, y: item.y || 0 }])),
          };
        } else {
          groupDragRef.current = null;
        }
      },
      onDragMove: e => {
        onEditorActivity?.(true);
        if (updateGroupDrag(element, e.target, { recordHistory: false, event: e.evt })) return;
        const snap = getDragSnap(element, e.target, e.evt);
        e.target.position({
          x: (element.x || 0) + snap.deltaX,
          y: (element.y || 0) + snap.deltaY,
        });
        setSnapLines(snap.lines);
      },
      onDragEnd: e => {
        onEditorActivity?.(true);
        const didGroupDrag = updateGroupDrag(element, e.target, { recordHistory: true, event: e.evt });
        if (didGroupDrag) {
          groupDragRef.current = null;
        } else {
          updateDrag(element, e.target, e.evt);
        }
        setSnapLines([]);
        setStageCursor(e.target, 'grab');
      },
      onTransformEnd: e => {
        onEditorActivity?.(true);
        if (selectedIds.length > 1) return;
        updateTransform(element, e.target);
      },
    };

    if (element.type === 'gate' || element.type === 'loadingBay') {
      const width = element.width || 120;
      const height = element.height || 56;
      const defaultColor = element.type === 'loadingBay' ? '#f97316' : '#039eff';
      const defaultLabel = element.type === 'loadingBay' ? 'Ladezone' : 'Tor';
      const label = element.label || defaultLabel;
      const keepUpright = shouldKeepTextUpright(element, label);
      return (
        <Group {...common}>
          <Rect
            width={width}
            height={height}
            fill={element.color || defaultColor}
            stroke={selectedIdSet.has(element.id) ? '#fff' : 'rgba(255,255,255,0.72)'}
            strokeWidth={selectedIdSet.has(element.id) ? 3 : 2}
            cornerRadius={6}
          />
          <Text
            x={width / 2}
            y={height / 2}
            offsetX={width / 2}
            offsetY={height / 2}
            rotation={keepUpright ? -(element.rotation || 0) : 0}
            text={label}
            width={width}
            height={height}
            align="center"
            verticalAlign="middle"
            fill="#fff"
            fontStyle="bold"
            fontSize={Math.max(12, Math.min(20, height * 0.34))}
            listening={false}
          />
        </Group>
      );
    }

    if (element.type === 'rect') {
      const width = element.width || 180;
      const height = element.height || 90;
      const text = getRectText(element);
      const keepUpright = text && shouldKeepTextUpright(element, text);
      return (
        <Group {...common}>
          <Rect
            width={width}
            height={height}
            fill={element.color || '#334155'}
            stroke={selectedIdSet.has(element.id) ? '#fff' : 'rgba(255,255,255,0.34)'}
            strokeWidth={selectedIdSet.has(element.id) ? 3 : 2}
            cornerRadius={4}
          />
          {getRectPatternElement(element, width, height)}
          {text && (
            <Text
              x={width / 2}
              y={height / 2}
              offsetX={width / 2}
              offsetY={height / 2}
              rotation={keepUpright ? -(element.rotation || 0) : 0}
              text={text}
              width={width}
              height={height}
              align="center"
              verticalAlign="middle"
              fill={element.textColor || '#fff'}
              fontStyle="bold"
              fontSize={Math.max(8, element.fontSize || 18)}
              wrap="word"
              listening={false}
            />
          )}
        </Group>
      );
    }

    if (element.type === 'line') {
      const strokeWidth = selectedIdSet.has(element.id) ? 5 : 3;
      const points = element.points || [0, 0, 180, 0];
      const isSelected = selectedIdSet.has(element.id);
      return (
        <Group {...common}>
          <Line
            points={points}
            stroke={element.stroke || '#f8fafc'}
            strokeWidth={strokeWidth}
            hitStrokeWidth={strokeWidth}
            lineCap="round"
            lineJoin="round"
          />
          {isSelected && [0, 1].map(index => {
            const point = index === 0
              ? { x: Number(points[0]) || 0, y: Number(points[1]) || 0 }
              : { x: Number(points[2]) || 0, y: Number(points[3]) || 0 };
            return (
              <Circle
                key={`handle-${index}`}
                x={point.x}
                y={point.y}
                radius={7}
                fill="#0b1020"
                stroke="#facc15"
                strokeWidth={2}
                draggable
                onMouseDown={e => {
                  e.cancelBubble = true;
                  onSelect(element.id, false);
                }}
                onDragStart={e => {
                  e.cancelBubble = true;
                  onEditorActivity?.(true);
                  lineEndpointDragRef.current = { layoutBefore: layout };
                  setStageCursor(e.target, 'grabbing');
                }}
                onDragMove={e => {
                  e.cancelBubble = true;
                  onEditorActivity?.(true);
                  updateLineEndpoint(element, index, { x: e.target.x(), y: e.target.y() }, {
                    recordHistory: false,
                    historyLayout: lineEndpointDragRef.current?.layoutBefore,
                    event: e.evt,
                    node: e.target,
                  });
                }}
                onDragEnd={e => {
                  e.cancelBubble = true;
                  onEditorActivity?.(true);
                  updateLineEndpoint(element, index, { x: e.target.x(), y: e.target.y() }, {
                    recordHistory: true,
                    historyLayout: lineEndpointDragRef.current?.layoutBefore,
                    event: e.evt,
                    node: e.target,
                  });
                  lineEndpointDragRef.current = null;
                  setSnapLines([]);
                  setStageCursor(e.target, 'grab');
                }}
              />
            );
          })}
        </Group>
      );
    }

    const metrics = getTextMetrics(element);
    const text = metrics.text;
    return (
      <Group {...common}>
        {text ? (
          <Text
            text={text}
            width={metrics.width}
            height={metrics.height}
            fill={element.color || '#f8fafc'}
            fontSize={element.fontSize || 28}
            fontStyle="bold"
            wrap="none"
          />
        ) : (
          <Rect
            width={EMPTY_TEXT_HANDLE_SIZE}
            height={EMPTY_TEXT_HANDLE_SIZE}
            fill="rgba(255,255,255,0.01)"
            stroke={selectedIdSet.has(element.id) ? '#facc15' : 'rgba(255,255,255,0.18)'}
            strokeWidth={selectedIdSet.has(element.id) ? 2 : 1}
            dash={[4, 3]}
          />
        )}
      </Group>
    );
  };

  const gridLines = useMemo(() => {
    const lines = [];
    const step = 50;
    for (let x = 0; x <= layout.stage.width; x += step) {
      lines.push(<Line key={`v-${x}`} points={[x, 0, x, layout.stage.height]} stroke="rgba(255,255,255,0.05)" strokeWidth={1} listening={false} />);
    }
    for (let y = 0; y <= layout.stage.height; y += step) {
      lines.push(<Line key={`h-${y}`} points={[0, y, layout.stage.width, y]} stroke="rgba(255,255,255,0.05)" strokeWidth={1} listening={false} />);
    }
    return lines;
  }, [layout.stage.height, layout.stage.width]);

  const isBackgroundTarget = target => target === target.getStage() || target.name() === 'background';

  const handleWheel = e => {
    e.evt.preventDefault();
    const stage = e.target.getStage();
    const pointer = stage.getPointerPosition();
    if (!pointer) return;

    setView(current => {
      const nextScale = clamp(
        current.scale * (e.evt.deltaY > 0 ? 1 / ZOOM_STEP : ZOOM_STEP),
        MIN_VIEW_SCALE,
        MAX_VIEW_SCALE
      );
      const mousePoint = {
        x: (pointer.x - current.x) / current.scale,
        y: (pointer.y - current.y) / current.scale,
      };

      return {
        scale: nextScale,
        x: pointer.x - mousePoint.x * nextScale,
        y: pointer.y - mousePoint.y * nextScale,
      };
    });
  };

  const handleSelectionStart = e => {
    if (!isBackgroundTarget(e.target) || e.evt.button !== 0) return;
    const stage = e.target.getStage();
    const pointer = getLayoutPointer(stage);
    if (!pointer) return;

    if (!hasSelectionModifier(e.evt)) {
      const stagePointer = stage.getPointerPosition();
      if (!stagePointer) return;
      panStartRef.current = {
        pointer: stagePointer,
        view,
        moved: false,
      };
      setIsPanning(true);
      return;
    }

    selectionStartRef.current = pointer;
    setSelectionBox({ x: pointer.x, y: pointer.y, width: 0, height: 0 });
  };

  const handleSelectionMove = e => {
    if (panStartRef.current) {
      const stage = e.target.getStage();
      const pointer = stage.getPointerPosition();
      if (!pointer) return;
      const pan = panStartRef.current;
      const deltaX = pointer.x - pan.pointer.x;
      const deltaY = pointer.y - pan.pointer.y;
      if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) {
        pan.moved = true;
      }
      setView({
        ...pan.view,
        x: pan.view.x + deltaX,
        y: pan.view.y + deltaY,
      });
      return;
    }

    if (!selectionStartRef.current) return;
    const stage = e.target.getStage();
    const pointer = getLayoutPointer(stage);
    if (!pointer) return;

    setSelectionBox(normalizeSelectionBox(selectionStartRef.current, pointer));
  };

  const handleSelectionEnd = e => {
    if (panStartRef.current) {
      const pan = panStartRef.current;
      panStartRef.current = null;
      setIsPanning(false);
      if (!pan.moved) onSelectMany([]);
      return;
    }

    const start = selectionStartRef.current;
    if (!start) return;

    selectionStartRef.current = null;
    const stage = e.target.getStage();
    const pointer = getLayoutPointer(stage) || start;
    const box = normalizeSelectionBox(start, pointer);
    setSelectionBox(null);

    if (box.width < 4 && box.height < 4) {
      if (!hasSelectionModifier(e.evt)) onSelectMany([]);
      return;
    }

    const ids = visibleElements
      .filter(element => {
        const node = shapeRefs.current[element.id];
        if (!node) return false;
        return boxesIntersect(box, node.getClientRect({ relativeTo: viewportGroupRef.current }));
      })
      .map(element => element.id);

    if (hasSelectionModifier(e.evt)) {
      onSelectMany([...new Set([...selectedIds, ...ids])]);
    } else {
      onSelectMany(ids);
    }
  };

  return (
    <main ref={canvasPanelRef} style={styles.canvasPanel} onScroll={reportViewportCenter}>
      <div ref={stageShellRef} style={styles.stageShell}>
        <Stage
          width={layout.stage.width}
          height={layout.stage.height}
          style={{ cursor: isPanning ? 'grabbing' : 'grab' }}
          onMouseDown={handleSelectionStart}
          onMouseMove={handleSelectionMove}
          onMouseUp={handleSelectionEnd}
          onMouseLeave={handleSelectionEnd}
          onWheel={handleWheel}
          onTouchStart={e => {
            if (isBackgroundTarget(e.target)) onSelectMany([]);
          }}
        >
          <Layer>
            <Group
              ref={viewportGroupRef}
              x={view.x}
              y={view.y}
              scaleX={view.scale}
              scaleY={view.scale}
            >
              <Rect name="background" width={layout.stage.width} height={layout.stage.height} fill="#0b1020" />
              {gridLines}
              {visibleElements.map(renderElement)}
              {snapLines.map((line, index) => (
                line.axis === 'length' ? (
                  <Line
                    key={`snap-length-${index}`}
                    points={[line.x1, line.value, line.x2, line.value]}
                    stroke="#facc15"
                    strokeWidth={1}
                    dash={[8, 6]}
                    listening={false}
                  />
                ) : line.axis === 'spacingX' ? (
                  <Line
                    key={`snap-spacing-x-${index}`}
                    points={[line.x1, line.y, line.x2, line.y]}
                    stroke="#facc15"
                    strokeWidth={1}
                    dash={[4, 4]}
                    listening={false}
                  />
                ) : line.axis === 'spacingY' ? (
                  <Line
                    key={`snap-spacing-y-${index}`}
                    points={[line.x, line.y1, line.x, line.y2]}
                    stroke="#facc15"
                    strokeWidth={1}
                    dash={[4, 4]}
                    listening={false}
                  />
                ) : line.axis === 'x' ? (
                  <Line
                    key={`snap-x-${index}`}
                    points={[line.value, -SNAP_LINE_PADDING, line.value, layout.stage.height + SNAP_LINE_PADDING]}
                    stroke="#facc15"
                    strokeWidth={1}
                    dash={[8, 6]}
                    listening={false}
                  />
                ) : (
                  <Line
                    key={`snap-y-${index}`}
                    points={[-SNAP_LINE_PADDING, line.value, layout.stage.width + SNAP_LINE_PADDING, line.value]}
                    stroke="#facc15"
                    strokeWidth={1}
                    dash={[8, 6]}
                    listening={false}
                  />
                )
              ))}
              {selectionBox && (
                <Rect
                  x={selectionBox.x}
                  y={selectionBox.y}
                  width={selectionBox.width}
                  height={selectionBox.height}
                  fill="rgba(59,130,246,0.14)"
                  stroke="#93c5fd"
                  strokeWidth={1}
                  dash={[6, 4]}
                  listening={false}
                />
              )}
              <Transformer
                ref={transformerRef}
                rotateEnabled
                rotationSnaps={ROTATION_SNAP_ANGLES}
                rotationSnapTolerance={ROTATION_SNAP_TOLERANCE}
                keepRatio={false}
                enabledAnchors={selectedIds.length > 1 ? [] : undefined}
                onTransformStart={() => {
                  onEditorActivity?.(true);
                  beginGroupTransform();
                }}
                onTransform={e => {
                  onEditorActivity?.(true);
                  applyIndividualGroupRotation(e.evt);
                }}
                onTransformEnd={e => {
                  onEditorActivity?.(true);
                  finishGroupTransform(e.evt);
                }}
                boundBoxFunc={(oldBox, newBox) => (
                  newBox.width < 10 || newBox.height < 10 ? oldBox : newBox
                )}
              />
            </Group>
          </Layer>
        </Stage>
      </div>
    </main>
  );
}

function LayoutEditor() {
  const [layout, setLayout] = useState(DEFAULT_LAYOUT);
  const [gates, setGates] = useState([]);
  const [loadingBays, setLoadingBays] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [activeFloor, setActiveFloor] = useState(0);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [status, setStatus] = useState('');
  const [lastAutoSavedAt, setLastAutoSavedAt] = useState(null);
  const [autosaveError, setAutosaveError] = useState('');
  const [autosaveTick, setAutosaveTick] = useState(0);
  const [dirtyVersion, setDirtyVersion] = useState(0);
  const [savedVersion, setSavedVersion] = useState(0);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [features, setFeatures] = useState({ layoutDownload: true, layoutUpload: true });
  const [baseRevision, setBaseRevision] = useState(null);
  const [activeEditors, setActiveEditors] = useState([]);
  const [editOverride, setEditOverride] = useState(false);
  const [presenceError, setPresenceError] = useState('');
  const [serviceUnavailable, setServiceUnavailable] = useState(false);
  const [hasLoadedLayout, setHasLoadedLayout] = useState(false);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const importInputRef = useRef(null);
  const dirtyVersionRef = useRef(0);
  const spawnPointRef = useRef({ x: 120, y: 120 });
  const clipboardRef = useRef([]);
  const editorClientIdRef = useRef(createElementId());
  const lastEditorActivityAtRef = useRef(0);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const isDirty = dirtyVersion !== savedVersion;
  const hasActiveEditorConflict = activeEditors.length > 0 && !editOverride;

  const selected = selectedIds.length === 1
    ? layout.elements.find(element => element.id === selectedIds[0] && isElementVisibleOnFloor(element, activeFloor))
    : null;
  const selectedElements = useMemo(() => {
    const selectedSet = new Set(selectedIds);
    return layout.elements.filter(element => selectedSet.has(element.id) && isElementVisibleOnFloor(element, activeFloor));
  }, [activeFloor, layout.elements, selectedIds]);

  const markDirty = useCallback(() => {
    lastEditorActivityAtRef.current = Date.now();
    setDirtyVersion(version => version + 1);
    setAutosaveError('');
  }, []);

  const markEditorActivity = useCallback((active = true) => {
    if (active) lastEditorActivityAtRef.current = Date.now();
  }, []);

  useEffect(() => {
    dirtyVersionRef.current = dirtyVersion;
  }, [dirtyVersion]);

  useEffect(() => {
    if (!hasLoadedLayout) return;
    if (!isDirty) {
      clearLocalDraft();
      return;
    }

    writeLocalDraft({
      layout,
      activeFloor,
      selectedIds,
      baseRevision,
      dirtyVersion,
    });
  }, [activeFloor, baseRevision, dirtyVersion, hasLoadedLayout, isDirty, layout, selectedIds]);

  const pushUndoSnapshot = useCallback(snapshot => {
    setUndoStack(stack => [...stack, normalizeLayout(snapshot)].slice(-HISTORY_LIMIT));
    setRedoStack([]);
  }, []);

  const applyLayoutChange = useCallback((updater, options = {}) => {
    const before = normalizeLayout(options.historyLayout || layout);
    const next = normalizeLayout(typeof updater === 'function' ? updater(layout) : updater);
    const nextMatchesCurrent = JSON.stringify(next) === JSON.stringify(layout);
    const historyDiffers = options.historyLayout && JSON.stringify(before) !== JSON.stringify(layout);
    if (nextMatchesCurrent && !historyDiffers) return;
    if (options.recordHistory !== false) {
      pushUndoSnapshot(before);
    } else {
      setRedoStack([]);
    }
    if (!nextMatchesCurrent) setLayout(next);
    markDirty();
  }, [layout, markDirty, pushUndoSnapshot]);

  const updateElement = useCallback((id, patch) => {
    applyLayoutChange(prev => ({
      ...prev,
      elements: prev.elements.map(element => (
        element.id === id ? { ...element, ...patch } : element
      )),
    }));
  }, [applyLayoutChange]);

  const updateElements = useCallback((patches, options = {}) => {
    applyLayoutChange(prev => ({
      ...prev,
      elements: prev.elements.map(element => (
        patches[element.id] ? { ...element, ...patches[element.id] } : element
      )),
    }), options);
  }, [applyLayoutChange]);

  const deleteElement = useCallback(id => {
    applyLayoutChange(prev => ({
      ...prev,
      elements: prev.elements.filter(element => element.id !== id),
    }));
    setSelectedIds(current => current.filter(selectedId => selectedId !== id));
  }, [applyLayoutChange]);

  const deleteSelectedElements = useCallback(() => {
    applyLayoutChange(prev => ({
      ...prev,
      elements: prev.elements.filter(element => !selectedIds.includes(element.id)),
    }));
    setSelectedIds([]);
  }, [applyLayoutChange, selectedIds]);

  const loadLayout = useCallback(() => {
    setStatus('Lade Layout...');
    Promise.all([
      apiFetch(`${API_BASE}/warehouse-layout`).then(res => {
        if ([502, 503, 504].includes(res.status)) throw createServiceUnavailableError();
        return res.json();
      }),
      apiFetch(`${API_BASE}/gates`).then(res => {
        if ([502, 503, 504].includes(res.status)) throw createServiceUnavailableError();
        return res.json();
      }),
      apiFetch(`${API_BASE}/loading-bays`).then(res => {
        if ([502, 503, 504].includes(res.status)) throw createServiceUnavailableError();
        return res.json();
      }),
    ])
      .then(async ([layoutResponse, gatesResponse, loadingBayResponse]) => {
        const serverLayout = normalizeLayout(layoutResponse.layout);
        const serverRevision = layoutResponse.revision || null;
        const draft = readLocalDraft();
        const shouldRestoreDraft = draft?.layout
          ? await showAppConfirm(
              `Es gibt einen lokalen, noch nicht gespeicherten Lagerlayout-Entwurf vom ${new Date(draft.savedAt || Date.now()).toLocaleString('de-DE')}. Soll dieser Entwurf wiederhergestellt werden?`,
              {
                title: 'Lokalen Entwurf wiederherstellen',
                confirmLabel: 'Entwurf laden',
                cancelLabel: 'Serverstand nutzen',
              }
            )
          : false;

        setLayout(shouldRestoreDraft ? normalizeLayout(draft.layout) : serverLayout);
        setGates(Array.isArray(gatesResponse) ? gatesResponse : []);
        setLoadingBays(Array.isArray(loadingBayResponse) ? loadingBayResponse : []);
        setSelectedIds(shouldRestoreDraft && Array.isArray(draft.selectedIds) ? draft.selectedIds : []);
        setActiveFloor(shouldRestoreDraft ? normalizeFloor(draft.activeFloor) : 0);
        setDirtyVersion(shouldRestoreDraft ? 1 : 0);
        setSavedVersion(0);
        setUndoStack([]);
        setRedoStack([]);
        setAutosaveError('');
        setServiceUnavailable(false);
        setBaseRevision(shouldRestoreDraft ? (draft.baseRevision ?? serverRevision) : serverRevision);
        setEditOverride(false);
        setHasLoadedLayout(true);
        if (shouldRestoreDraft) {
          setStatus('Lokaler Entwurf wiederhergestellt. Bitte speichern, sobald der Service erreichbar ist.');
        } else {
          clearLocalDraft();
          setStatus(layoutResponse.updated_at ? `Geladen: ${new Date(layoutResponse.updated_at).toLocaleString('de-DE')}` : 'Standardlayout geladen');
        }
      })
      .catch(error => {
        const draft = readLocalDraft();
        if (draft?.layout) {
          setLayout(normalizeLayout(draft.layout));
          setSelectedIds(Array.isArray(draft.selectedIds) ? draft.selectedIds : []);
          setActiveFloor(normalizeFloor(draft.activeFloor));
          setDirtyVersion(1);
          setSavedVersion(0);
          setBaseRevision(draft.baseRevision || null);
          setServiceUnavailable(true);
          setHasLoadedLayout(true);
          setStatus('Service aktuell nicht erreichbar. Lokaler Entwurf wurde wiederhergestellt und kann nach dem Neustart gespeichert werden.');
          return;
        }
        setServiceUnavailable(isServiceUnavailableError(error));
        setHasLoadedLayout(true);
        setStatus('Layout konnte nicht geladen werden.');
      });
  }, []);

  useEffect(() => {
    loadLayout();
    apiFetch(`${API_BASE}/public/settings`)
      .then(res => res.json())
      .then(data => setFeatures({
        layoutDownload: data.layout_download_enabled !== false,
        layoutUpload: data.layout_upload_enabled !== false,
      }))
      .catch(() => {});
  }, [loadLayout]);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (activeEditors.length === 0 && editOverride) {
      setEditOverride(false);
    }
  }, [activeEditors.length, editOverride]);

  useEffect(() => {
    const interval = window.setInterval(() => setAutosaveTick(tick => tick + 1), 30000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleBeforeUnload = event => {
      if (!isDirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    let stopped = false;
    const clientId = editorClientIdRef.current;

    const sendHeartbeat = () => {
      const active = Date.now() - lastEditorActivityAtRef.current <= EDITOR_ACTIVE_WINDOW_MS;
      apiFetch(`${API_BASE}/warehouse-layout/edit-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, active, override: editOverride }),
      })
        .then(res => res.json())
        .then(data => {
          if (stopped) return;
          setActiveEditors(Array.isArray(data.editors) ? data.editors : []);
          setPresenceError('');
          setServiceUnavailable(false);
        })
        .catch(error => {
          if (!stopped) {
            setPresenceError('Bearbeitungsstatus konnte nicht aktualisiert werden.');
            if (isServiceUnavailableError(error)) setServiceUnavailable(true);
          }
        });
    };

    sendHeartbeat();
    const interval = window.setInterval(sendHeartbeat, EDIT_SESSION_HEARTBEAT_MS);
    return () => {
      stopped = true;
      window.clearInterval(interval);
      apiFetch(`${API_BASE}/warehouse-layout/edit-session?clientId=${encodeURIComponent(clientId)}`, { method: 'DELETE' }).catch(() => {});
    };
  }, [editOverride]);

  const duplicateSelectedElements = useCallback(() => {
    if (selectedIds.length === 0) return;
    const selectedSet = new Set(selectedIds);
    const hasActiveSelection = layout.elements.some(element => selectedSet.has(element.id) && isElementVisibleOnFloor(element, activeFloor));
    if (!hasActiveSelection) return;
    applyLayoutChange(prev => {
      const nextIds = [];
      const nextElements = prev.elements.flatMap(element => {
        if (!selectedSet.has(element.id) || !isElementVisibleOnFloor(element, activeFloor)) {
          return [element];
        }
        const duplicate = {
          ...element,
          id: createElementId(),
          x: (element.x || 0) + DUPLICATE_OFFSET,
          y: (element.y || 0) + DUPLICATE_OFFSET,
          floor: normalizeFloor(element.floor),
        };
        nextIds.push(duplicate.id);
        return [element, duplicate];
      });
      if (nextIds.length > 0) {
        window.setTimeout(() => setSelectedIds(nextIds), 0);
      }
      return { ...prev, elements: nextElements };
    });
  }, [activeFloor, applyLayoutChange, layout.elements, selectedIds]);

  const copySelectedElements = useCallback(() => {
    if (selectedIds.length === 0) return;
    const selectedSet = new Set(selectedIds);
    clipboardRef.current = layout.elements
      .filter(element => selectedSet.has(element.id) && isElementVisibleOnFloor(element, activeFloor))
      .map(element => ({ ...element }));
  }, [activeFloor, layout.elements, selectedIds]);

  const pasteCopiedElements = useCallback(() => {
    const copied = clipboardRef.current;
    if (!copied.length) return;
    const pastedIds = [];
    applyLayoutChange(prev => {
      const pasted = copied.map(element => {
        const next = {
          ...element,
          id: createElementId(),
          floor: activeFloor,
        };
        pastedIds.push(next.id);
        return next;
      });
      window.setTimeout(() => setSelectedIds(pastedIds), 0);
      return { ...prev, elements: [...prev.elements, ...pasted] };
    });
  }, [activeFloor, applyLayoutChange]);

  const moveElementLayer = useCallback((id, direction) => {
    applyLayoutChange(prev => {
      const index = prev.elements.findIndex(element => element.id === id);
      if (index < 0) return prev;
      const sameFloorIndexes = prev.elements
        .map((item, itemIndex) => ({ item, itemIndex }))
        .filter(entry => isElementVisibleOnFloor(entry.item, activeFloor))
        .map(entry => entry.itemIndex);
      const currentFloorPosition = sameFloorIndexes.indexOf(index);
      if (currentFloorPosition < 0) return prev;

      let targetFloorPosition = currentFloorPosition;
      if (direction === 'back') targetFloorPosition = Math.max(0, currentFloorPosition - 1);
      if (direction === 'bottom') targetFloorPosition = 0;
      if (direction === 'forward') targetFloorPosition = Math.min(sameFloorIndexes.length - 1, currentFloorPosition + 1);
      if (direction === 'top') targetFloorPosition = sameFloorIndexes.length - 1;
      if (targetFloorPosition === currentFloorPosition) return prev;

      const floorElements = sameFloorIndexes.map(itemIndex => prev.elements[itemIndex]);
      const [moved] = floorElements.splice(currentFloorPosition, 1);
      floorElements.splice(targetFloorPosition, 0, moved);
      let floorCursor = 0;
      const elements = prev.elements.map(item => (
        isElementVisibleOnFloor(item, activeFloor) ? floorElements[floorCursor++] : item
      ));
      return { ...prev, elements };
    });
  }, [activeFloor, applyLayoutChange]);

  const undoLayoutChange = useCallback(() => {
    if (undoStack.length === 0) return;
    const previous = normalizeLayout(undoStack[undoStack.length - 1]);
    setUndoStack(stack => stack.slice(0, -1));
    setRedoStack(stack => [normalizeLayout(layout), ...stack].slice(0, HISTORY_LIMIT));
    setLayout(previous);
    setSelectedIds([]);
    markDirty();
  }, [layout, markDirty, undoStack]);

  const redoLayoutChange = useCallback(() => {
    if (redoStack.length === 0) return;
    const next = normalizeLayout(redoStack[0]);
    setRedoStack(stack => stack.slice(1));
    setUndoStack(stack => [...stack, normalizeLayout(layout)].slice(-HISTORY_LIMIT));
    setLayout(next);
    setSelectedIds([]);
    markDirty();
  }, [layout, markDirty, redoStack]);

  useEffect(() => {
    const onKeyDown = e => {
      if (isEditableTarget(e.target)) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          redoLayoutChange();
        } else {
          undoLayoutChange();
        }
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c' && selectedIds.length > 0) {
        e.preventDefault();
        copySelectedElements();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
        e.preventDefault();
        pasteCopiedElements();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd' && selectedIds.length > 0) {
        e.preventDefault();
        duplicateSelectedElements();
        return;
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedIds.length > 0) {
        e.preventDefault();
        deleteSelectedElements();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [copySelectedElements, deleteSelectedElements, duplicateSelectedElements, pasteCopiedElements, redoLayoutChange, selectedIds.length, undoLayoutChange]);

  const selectElement = useCallback((id, additive = false) => {
    if (!id) {
      setSelectedIds([]);
      return;
    }

    setSelectedIds(current => {
      if (!additive) return [id];
      return current.includes(id)
        ? current.filter(selectedId => selectedId !== id)
        : [...current, id];
    });
  }, []);

  const selectElementsByType = useCallback(type => {
    const ids = layout.elements
      .filter(element => element.type === type && isElementVisibleOnFloor(element, activeFloor))
      .map(element => element.id);
    setSelectedIds(ids);
  }, [activeFloor, layout.elements]);

  const changeFloor = useCallback(floor => {
    setActiveFloor(normalizeFloor(floor));
    setSelectedIds([]);
  }, []);

  const reloadLayout = useCallback(async () => {
    if (isDirty) {
      const confirmed = await showAppConfirm(
        'Es gibt ungespeicherte Aenderungen am Lagerlayout. Neu laden verwirft den aktuellen lokalen Stand. Trotzdem neu laden?',
        {
          title: 'Layout neu laden',
          confirmLabel: 'Neu laden',
          danger: true,
        }
      );
      if (!confirmed) return;
    }
    loadLayout();
  }, [isDirty, loadLayout]);

  const updateSpawnPoint = useCallback(point => {
    spawnPointRef.current = point;
  }, []);

  const addElement = type => {
    const draft = createElement(type, gates, loadingBays, activeFloor, spawnPointRef.current);
    const spawnFrame = getElementSpawnFrame(draft);
    const spawnX = (spawnPointRef.current.x || 0) - spawnFrame.width / 2 - spawnFrame.offsetX;
    const spawnY = (spawnPointRef.current.y || 0) - spawnFrame.height / 2 - spawnFrame.offsetY;
    const element = {
      ...draft,
      x: clamp(spawnX, 0, Math.max(0, layout.stage.width - spawnFrame.width)),
      y: clamp(spawnY, 0, Math.max(0, layout.stage.height - spawnFrame.height)),
    };
    applyLayoutChange(prev => ({ ...prev, elements: [...prev.elements, element] }));
    setSelectedIds([element.id]);
  };

  const updateStage = patch => {
    applyLayoutChange(prev => ({
      ...prev,
      stage: {
        ...prev.stage,
        ...patch,
      },
    }));
  };

  const isTablet = viewportWidth < 1100;
  const isMobile = viewportWidth < 760;

  const saveLayout = useCallback(async (options = {}) => {
    const { automatic = false, layoutSnapshot = layout, versionSnapshot = dirtyVersion } = options;
    if (hasActiveEditorConflict) {
      if (automatic) {
        setAutosaveError('Automatisches Speichern pausiert: Jemand anderes bearbeitet gerade aktiv.');
      } else {
        setStatus('Speichern blockiert: Jemand anderes bearbeitet gerade aktiv.');
      }
      return;
    }
    setSaving(true);
    if (!automatic) setStatus('Speichere Layout...');

    try {
      const response = await apiFetch(`${API_BASE}/warehouse-layout`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ layout: layoutSnapshot, baseRevision }),
      });
      setServiceUnavailable(false);

      if ([502, 503, 504].includes(response.status)) {
        throw createServiceUnavailableError();
      }

      const data = await response.json();
      if (response.status === 409 && data?.code === 'LAYOUT_CONFLICT') {
        setBaseRevision(data.revision || null);
        throw new Error('Das Lagerlayout wurde in einem anderen Tab gespeichert. Bitte neu laden, bevor du weiter speicherst.');
      }
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      if (dirtyVersionRef.current === versionSnapshot) {
        setLayout(normalizeLayout(data.layout));
      }
      setBaseRevision(data.revision || null);
      setSavedVersion(current => Math.max(current, versionSnapshot));
      if (automatic) {
        setLastAutoSavedAt(new Date(data.updated_at || Date.now()));
        setAutosaveError('');
      } else {
        setStatus(`Gespeichert: ${new Date(data.updated_at).toLocaleString('de-DE')}`);
      }
      clearLocalDraft();
    } catch (err) {
      if (isServiceUnavailableError(err)) {
        setServiceUnavailable(true);
        if (automatic) {
          setAutosaveError(`${SAVE_UNAVAILABLE_MESSAGE} Lokaler Entwurf bleibt im Browser gesichert.`);
        } else {
          setStatus(SAVE_UNAVAILABLE_MESSAGE);
          showAppAlert(`${SAVE_UNAVAILABLE_MESSAGE}\n\nDu kannst die Seite offen lassen und danach erneut speichern. Ein lokaler Entwurf bleibt im Browser gesichert.`, {
            title: 'Speichern aktuell nicht moeglich',
            confirmLabel: 'Verstanden',
            danger: true,
          });
        }
        return;
      }
      if (automatic) {
        setAutosaveError(err.message || 'Automatisches Speichern fehlgeschlagen.');
      } else {
        setStatus(err.message || 'Speichern fehlgeschlagen.');
      }
    } finally {
      setSaving(false);
    }
  }, [baseRevision, dirtyVersion, hasActiveEditorConflict, layout]);

  useEffect(() => {
    if (!isDirty || saving || importing || hasActiveEditorConflict || serviceUnavailable) return undefined;
    const layoutSnapshot = normalizeLayout(layout);
    const versionSnapshot = dirtyVersion;
    const timer = window.setTimeout(() => {
      saveLayout({ automatic: true, layoutSnapshot, versionSnapshot });
    }, AUTOSAVE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [dirtyVersion, hasActiveEditorConflict, importing, isDirty, layout, saveLayout, saving, serviceUnavailable]);

  useEffect(() => {
    if (!serviceUnavailable || !isDirty || saving || importing || hasActiveEditorConflict) return undefined;
    const layoutSnapshot = normalizeLayout(layout);
    const versionSnapshot = dirtyVersion;
    const timer = window.setTimeout(() => {
      saveLayout({ automatic: true, layoutSnapshot, versionSnapshot });
    }, SERVICE_RETRY_MS);
    return () => window.clearTimeout(timer);
  }, [dirtyVersion, hasActiveEditorConflict, importing, isDirty, layout, saveLayout, saving, serviceUnavailable]);

  const downloadLayout = () => {
    if (!features.layoutDownload) {
      setStatus('Layout-Download ist in deiner Lizenz nicht freigeschaltet.');
      return;
    }
    const file = new Blob(
      [JSON.stringify(createLayoutExport(layout), null, 2)],
      { type: 'application/json' }
    );
    const url = window.URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = getExportFileName();
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    setStatus('Layout-Download wurde erstellt.');
  };

  const uploadImportedLayout = async importedLayout => {
    if (hasActiveEditorConflict) {
      setStatus('Upload blockiert: Jemand anderes bearbeitet gerade aktiv.');
      return;
    }
    setImporting(true);
    setStatus('Importiere Layout...');

    try {
      const response = await apiFetch(`${API_BASE}/warehouse-layout`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ layout: importedLayout, baseRevision }),
      });
      setServiceUnavailable(false);

      if ([502, 503, 504].includes(response.status)) {
        throw createServiceUnavailableError();
      }

      const data = await response.json();
      if (response.status === 409 && data?.code === 'LAYOUT_CONFLICT') {
        setBaseRevision(data.revision || null);
        throw new Error('Das Lagerlayout wurde in einem anderen Tab gespeichert. Bitte neu laden und den Upload danach erneut starten.');
      }
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setLayout(normalizeLayout(data.layout));
      setSelectedIds([]);
      setDirtyVersion(0);
      setSavedVersion(0);
      setBaseRevision(data.revision || null);
      setUndoStack([]);
      setRedoStack([]);
      setAutosaveError('');
      clearLocalDraft();
      setStatus(`Importiert und gespeichert: ${new Date(data.updated_at).toLocaleString('de-DE')}`);
    } catch (err) {
      if (isServiceUnavailableError(err)) {
        setServiceUnavailable(true);
        setStatus(SAVE_UNAVAILABLE_MESSAGE);
        showAppAlert(`${SAVE_UNAVAILABLE_MESSAGE}\n\nDer Upload wurde nicht gespeichert. Bitte versuche es erneut, wenn der Service wieder erreichbar ist.`, {
          title: 'Upload aktuell nicht moeglich',
          confirmLabel: 'Verstanden',
          danger: true,
        });
      } else {
        setStatus(err.message || 'Upload fehlgeschlagen.');
      }
    } finally {
      setImporting(false);
    }
  };

  const importLayoutFile = async event => {
    if (!features.layoutUpload) {
      setStatus('Layout-Upload ist in deiner Lizenz nicht freigeschaltet.');
      return;
    }
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    let importedLayout;
    try {
      const content = await file.text();
      importedLayout = getLayoutFromImport(JSON.parse(content));
    } catch (err) {
      setStatus('Upload fehlgeschlagen: Datei ist kein gültiges Layout.');
      return;
    }

    const confirmed = await showAppConfirm(
      'Der Upload überschreibt das aktuelle Warehouse Layout für alle Nutzer. Möchtest du das Layout wirklich übernehmen?',
      {
        title: 'Layout übernehmen',
        confirmLabel: 'Übernehmen',
        danger: true,
      }
    );
    if (!confirmed) {
      setStatus('Upload abgebrochen. Das aktuelle Layout wurde nicht geändert.');
      return;
    }

    await uploadImportedLayout(importedLayout);
  };

  const autosaveLabel = useMemo(() => {
    void autosaveTick;
    if (hasActiveEditorConflict) return 'Autosave pausiert: anderer Nutzer bearbeitet aktiv';
    if (serviceUnavailable && isDirty) return 'Autosave pausiert: Service aktuell nicht erreichbar';
    if (autosaveError) return autosaveError;
    const base = autosaveLabelFor(lastAutoSavedAt);
    return isDirty ? `${base} - Aenderungen offen` : base;
  }, [autosaveError, autosaveTick, hasActiveEditorConflict, isDirty, lastAutoSavedAt, serviceUnavailable]);

  return (
    <div>
      {isDirty && (serviceUnavailable || autosaveError) && (
        <div style={styles.updateToast} role="status" aria-live="polite">
          <span style={styles.updateToastTitle}>Layout aktuell nicht gespeichert.</span>
          <span>Beim Neuladen der Seite gehen ungesicherte Aenderungen verloren, falls kein lokaler Entwurf wiederhergestellt werden kann.</span>
          {serviceUnavailable && <span>Speichern ist gerade nicht moeglich. Bitte in ca. 1 Minute erneut versuchen.</span>}
        </div>
      )}
      {(hasActiveEditorConflict || presenceError) && (
        <div style={styles.conflictBanner}>
          <p style={styles.conflictText}>
            {hasActiveEditorConflict
              ? `${activeEditors.map(editor => editor.username || 'Ein anderer Nutzer').join(', ')} bearbeitet das Lagerlayout gerade aktiv. Speichern und Autosave sind pausiert, bis die Bearbeitung endet oder du bewusst uebernimmst.`
              : presenceError}
          </p>
          {hasActiveEditorConflict && (
            <button type="button" style={styles.tinyButton} onClick={() => setEditOverride(true)}>
              Trotzdem bearbeiten
            </button>
          )}
        </div>
      )}
      <div
        style={{
          ...styles.page,
          gridTemplateColumns: isMobile
            ? '1fr'
            : (isTablet ? 'minmax(0, 1fr)' : styles.page.gridTemplateColumns),
        }}
      >
        <ElementToolbar
          onAdd={addElement}
          onSave={saveLayout}
          saveDisabled={hasActiveEditorConflict}
          onReload={reloadLayout}
          onDownload={downloadLayout}
          onImportClick={() => importInputRef.current?.click()}
          onPrintClick={() => setPrintModalOpen(true)}
          onSelectType={selectElementsByType}
          saving={saving}
          importing={importing}
          features={features}
          activeFloor={activeFloor}
          onFloorChange={changeFloor}
          autosaveLabel={autosaveLabel}
        />
        <input
          ref={importInputRef}
          type="file"
          accept="application/json,.json"
          style={{ display: 'none' }}
          onChange={importLayoutFile}
        />
        <LayoutCanvas
          layout={layout}
          selectedIds={selectedIds}
          activeFloor={activeFloor}
          onSelect={selectElement}
          onSelectMany={setSelectedIds}
          onUpdate={updateElement}
          onUpdateMany={updateElements}
          onViewportChange={updateSpawnPoint}
          onEditorActivity={markEditorActivity}
        />
        <ElementPropertiesPanel
          selected={selected}
          selectedElements={selectedElements}
          selectedCount={selectedIds.length}
          gates={gates}
          loadingBays={loadingBays}
          onUpdate={updateElement}
          onUpdateMany={updateElements}
          onDelete={deleteElement}
          onDeleteSelected={deleteSelectedElements}
          stage={layout.stage}
          onStageUpdate={updateStage}
          onMoveLayer={moveElementLayer}
        />
      </div>
      <div style={styles.status}>{status}</div>
      {printModalOpen && (
        <WarehousePrintModal
          layout={layout}
          activeFloor={activeFloor}
          onClose={() => setPrintModalOpen(false)}
        />
      )}
    </div>
  );
}

export default LayoutEditor;
